import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import DealerLayout from '../components/DealerLayout';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import { C } from '../theme';

const STATUS_CFG = {
  overdue:  { label: 'Overdue',  bg: '#FEE2E2', color: '#991B1B', dot: '#EF4444', border: '#FECACA' },
  due_soon: { label: 'Due Soon', bg: '#FEF3C7', color: '#92400E', dot: '#F59E0B', border: '#FDE68A' },
  upcoming: { label: 'Upcoming', bg: '#D1FAE5', color: '#065F46', dot: '#10B981', border: '#6EE7B7' },
};

const ROW_BG = {
  overdue:  ['#FFF5F5', '#FFF1F1'],
  due_soon: ['#FFFDF5', '#FFFBEB'],
  upcoming: [C.white,   '#FAFFF9'],
};

const TABS = [
  { key: 'all',             label: 'All'                     },
  { key: 'dealer_clients',  label: 'Dealer Clients'          },
  { key: 'dealer_requests', label: 'Dealer Renewal Requests' },
  { key: 'overdue',         label: 'Overdue'                 },
  { key: 'due_soon',        label: 'Due Soon'                },
  { key: 'upcoming',        label: 'Upcoming'                },
  { key: 'cancelled',       label: 'Cancelled Subscription'  },
];

// Tabs a dealer never sees (admin-only splits). Completed renewals live in the
// separate Renewal History module, not here.
const ADMIN_ONLY_TABS = ['dealer_clients', 'dealer_requests'];

const REQ_CFG = {
  pending:   { label: 'Pending',   bg: '#FEF3C7', color: '#92400E', border: '#FDE68A', dot: '#F59E0B' },
  completed: { label: 'Completed', bg: '#D1FAE5', color: '#065F46', border: '#6EE7B7', dot: '#10B981' },
  rejected:  { label: 'Rejected',  bg: '#FEE2E2', color: '#991B1B', border: '#FECACA', dot: '#EF4444' },
};

function warrantyExpired(dateStr) {
  if (!dateStr) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return new Date(dateStr) < today;
}

export default function RenewalList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isDealer = user?.role === 'dealer';
  const LayoutComp = isDealer ? DealerLayout : Layout;
  const creditListPath = isDealer ? '/dealer/credit-list' : '/credit-list';
  const [items,       setItems]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [tab,         setTab]         = useState('all');
  const [search,      setSearch]      = useState('');
  const [expanded,    setExpanded]    = useState(null);
  // Admin defaults to the actionable "due this month" view. A dealer needs to see
  // every renewable device to raise a request, so they default to the full range.
  const [dateFilter,  setDateFilter]  = useState(isDealer ? 'all' : 'this_month');
  const [customFrom,  setCustomFrom]  = useState('');
  const [customTo,    setCustomTo]    = useState('');
  const [renewItem,   setRenewItem]   = useState(null);
  const [renewing,    setRenewing]    = useState(false);
  const [toast,       setToast]       = useState(null);

  // Inline AMC edit (dealer clients tab)
  const [editingAmc, setEditingAmc] = useState(null); // { uid, pkg_item_id, value }
  const [savingAmc,  setSavingAmc]  = useState(false);

  // Renewal history
  const [history,    setHistory]    = useState([]);

  // WhatsApp reminders (admin only)
  const [reminder,   setReminder]   = useState(null);   // { rows: [...] }
  const [reminderMsg, setReminderMsg] = useState('');
  const [sendingRem, setSendingRem] = useState(false);
  const [remResults, setRemResults] = useState(null);   // { sent, failed, results }

  // Dealer renewal requests
  const [requests,   setRequests]   = useState([]);
  const [wallet,     setWallet]     = useState(null);  // dealer wallet balance
  const [reqItem,    setReqItem]    = useState(null);  // dealer: item being requested
  const [reqNote,    setReqNote]    = useState('');
  const [requesting, setRequesting] = useState(false);
  const [actionReq,  setActionReq]  = useState(null);  // admin: { req, mode: 'complete'|'reject' }
  const [actionNote, setActionNote] = useState('');
  const [acting,     setActing]     = useState(false);

  // Cancel
  const [cancelItem,   setCancelItem]   = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling,   setCancelling]   = useState(false);

  // Renew with swap
  const [renewInfo,             setRenewInfo]             = useState(null);
  const [renewLoad,             setRenewLoad]             = useState(false);
  const [renewSwaps,            setRenewSwaps]            = useState({});
  const [renewSwapReasons,      setRenewSwapReasons]      = useState({});
  const [renewAdditionalCharge, setRenewAdditionalCharge] = useState('');
  const [renewNote,             setRenewNote]             = useState('');

  // Restore with swap
  const [restoreItem,      setRestoreItem]      = useState(null);
  const [restoreInfo,      setRestoreInfo]      = useState(null);
  const [restoreLoad,      setRestoreLoad]      = useState(false);
  const [swaps,            setSwaps]            = useState({});   // { pkg_item_id: new_inv_id }
  const [swapReasons,      setSwapReasons]      = useState({});   // { pkg_item_id: reason }
  const [additionalCharge, setAdditionalCharge] = useState('');
  const [restoreNote,      setRestoreNote]      = useState('');
  const [restoring,        setRestoring]        = useState(false);

  const REPLACE_REASONS = [
    { value: 'damaged',     label: 'Damaged'     },
    { value: 'expired',     label: 'Expired'     },
    { value: 'not_working', label: 'Not Working' },
    { value: 'lost',        label: 'Lost'        },
    { value: 'other',       label: 'Other'       },
  ];

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 4500);
  };

  // ── WhatsApp reminders ──────────────────────────────────────────────────────
  const DEFAULT_REM_MSG =
    'Hi {client}, this is a reminder from {business}. Your {category} (Serial: {serial}) ' +
    'is due for renewal on {due_date}. Renewal amount: ₹{amount}. Please renew to avoid interruption. Thank you.';

  const openReminder = (rows) => {
    const list = rows.filter(r => !r.cancelled);
    if (list.length === 0) { showToast('error', 'No renewals to remind about here.'); return; }
    setReminder({ rows: list });
    setReminderMsg(DEFAULT_REM_MSG);
    setRemResults(null);
  };

  const sendReminders = async () => {
    if (!reminder) return;
    setSendingRem(true);
    setRemResults(null);
    try {
      const r = await api.post('/api/renewal-list/send-reminders', {
        targets: reminder.rows.map(x => ({ mapping_db_id: x.mapping_db_id, inv_db_id: x.inv_db_id })),
        message_template: reminderMsg.trim() || null,
      });
      setRemResults(r.data);
      if (r.data.sent > 0 && r.data.failed === 0) showToast('success', `Sent ${r.data.sent} reminder${r.data.sent !== 1 ? 's' : ''} ✓`);
      else if (r.data.sent > 0) showToast('success', `Sent ${r.data.sent}, ${r.data.failed} failed — see details.`);
      else showToast('error', `Could not send — ${r.data.failed} failed.`);
    } catch (e) {
      showToast('error', e?.response?.data?.detail || 'Failed to send reminders.');
    } finally { setSendingRem(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/renewal-list');
      setItems(r.data);
    } catch {} finally { setLoading(false); }
  }, []);

  const loadRequests = useCallback(async () => {
    try {
      const r = await api.get('/api/dealer-renewal-requests');
      setRequests(r.data);
    } catch {}
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const r = await api.get('/api/renewal-history');
      setHistory(r.data);
    } catch {}
  }, []);

  const loadWallet = useCallback(async () => {
    if (!isDealer) return;
    try {
      const r = await api.get('/api/dealer/credits');
      setWallet(r.data.balance ?? 0);
    } catch {}
  }, [isDealer]);

  useEffect(() => { load(); loadRequests(); loadWallet(); loadHistory(); },
    [load, loadRequests, loadWallet, loadHistory]);

  // ── Dealer: raise a renewal request ──────────────────────────────────────────

  const submitRequest = async () => {
    if (!reqItem) return;
    setRequesting(true);
    try {
      await api.post('/api/dealer-renewal-requests', {
        mapping_db_id: reqItem.mapping_db_id,
        inv_db_id:     reqItem.inv_db_id,
        notes:         reqNote.trim() || null,
      });
      setReqItem(null);
      setReqNote('');
      await Promise.all([load(), loadRequests(), loadWallet()]);
      showToast('success', `Renewal request sent to admin for ${reqItem.client_name}.`);
    } catch (e) {
      showToast('error', e?.response?.data?.detail || 'Could not raise renewal request.');
    } finally { setRequesting(false); }
  };

  // ── Admin: complete / reject a dealer renewal request ────────────────────────

  const submitAction = async () => {
    if (!actionReq) return;
    setActing(true);
    try {
      const { req, mode } = actionReq;
      await api.post(`/api/dealer-renewal-requests/${req.id}/${mode}`, {
        admin_notes: actionNote.trim() || null,
      });
      setActionReq(null);
      setActionNote('');
      await Promise.all([load(), loadRequests(), loadHistory()]);
      showToast('success', mode === 'complete'
        ? `${req.request_id} completed — moved to Renewal History. ${req.dealer_name} has been notified.`
        : `${req.request_id} rejected — ₹${(req.dealer_amount || 0).toLocaleString('en-IN')} refunded to ${req.dealer_name}.`);
    } catch (e) {
      showToast('error', e?.response?.data?.detail || 'Action failed.');
    } finally { setActing(false); }
  };

  const saveAmc = async () => {
    if (!editingAmc) return;
    setSavingAmc(true);
    try {
      const val = editingAmc.value === '' ? null : parseFloat(editingAmc.value);
      await api.patch('/api/renewal-list/amc-override', {
        pkg_item_id: editingAmc.pkg_item_id,
        amc_amount:  isNaN(val) ? null : val,
      });
      setEditingAmc(null);
      await load();
      showToast('success', 'AMC amount updated.');
    } catch {
      showToast('error', 'Failed to update AMC amount.');
    } finally { setSavingAmc(false); }
  };

  // ── Renew Now ────────────────────────────────────────────────────────────────

  const openRenewModal = async (item) => {
    setRenewItem(item);
    setRenewSwaps({});
    setRenewSwapReasons({});
    setRenewAdditionalCharge('');
    setRenewNote('');
    setRenewInfo(null);
    setRenewLoad(true);
    try {
      const r = await api.get(`/api/renewal-list/${item.mapping_db_id}/package-info?inv_id=${item.inv_db_id || 0}`);
      setRenewInfo(r.data);
    } catch (e) {
      showToast('error', e?.response?.data?.detail || 'Could not load package items.');
      setRenewItem(null);
    } finally { setRenewLoad(false); }
  };

  const closeRenewModal = () => {
    setRenewItem(null);
    setRenewInfo(null);
    setRenewSwaps({});
    setRenewSwapReasons({});
    setRenewAdditionalCharge('');
    setRenewNote('');
  };

  const setRenewSwap = (pkgItemId, newInvId) => {
    setRenewSwaps(prev => {
      if (!newInvId) { const n = { ...prev }; delete n[pkgItemId]; return n; }
      return { ...prev, [pkgItemId]: newInvId };
    });
    if (!newInvId) {
      setRenewSwapReasons(prev => { const n = { ...prev }; delete n[pkgItemId]; return n; });
    } else {
      setRenewSwapReasons(prev => ({ ...prev, [pkgItemId]: prev[pkgItemId] || 'damaged' }));
    }
  };

  const setRenewSwapReason = (pkgItemId, reason) => {
    setRenewSwapReasons(prev => ({ ...prev, [pkgItemId]: reason }));
  };

  const confirmRenew = async () => {
    if (!renewItem || !renewInfo) return;
    setRenewing(true);
    try {
      const replacements = Object.entries(renewSwaps).map(([pkg_item_id, new_inv_id]) => ({
        pkg_item_id: Number(pkg_item_id),
        new_inv_id:  Number(new_inv_id),
        reason:      renewSwapReasons[pkg_item_id] || 'other',
      }));
      await api.post('/api/renewals', {
        client_id:         renewItem.client_id,
        installer_id:      renewItem.installer_id,
        package_id:        renewItem.package_db_id,
        amc_amount:        renewItem.amc_amount,
        additional_charge: parseFloat(renewAdditionalCharge) || 0,
        category_name:     renewItem.category_name,
        type_name:         renewItem.type_name,
        source_mapping_id: renewItem.mapping_id,
        // Links the credit entry to the device + original mapping, so paying it
        // extends this subscription by one more period.
        inv_db_id:            renewItem.inv_db_id,
        source_mapping_db_id: renewItem.mapping_db_id,
        replacements,
        notes: renewNote.trim() || null,
      });
      closeRenewModal();
      await load();
      showToast('success', `Renewal added to Credit List for ${renewItem.client_name}.`);
    } catch (e) {
      showToast('error', e?.response?.data?.detail || 'Renewal failed.');
    } finally { setRenewing(false); }
  };

  // ── Cancel ───────────────────────────────────────────────────────────────────

  const confirmCancel = async () => {
    if (!cancelItem) return;
    setCancelling(true);
    try {
      await api.post('/api/renewal-cancellations', {
        mapping_db_id: cancelItem.mapping_db_id,
        inv_db_id:     cancelItem.inv_db_id,
        reason:        cancelReason.trim() || null,
      });
      await load();
      setCancelItem(null);
      setCancelReason('');
      showToast('success', `Subscription cancelled for ${cancelItem.client_name} — ${cancelItem.category_name}.`);
    } catch (e) {
      showToast('error', e?.response?.data?.detail || 'Cancel failed.');
    } finally { setCancelling(false); }
  };

  // ── Open Restore Modal ───────────────────────────────────────────────────────

  const openRestoreModal = async (item) => {
    setRestoreItem(item);
    setSwaps({});
    setSwapReasons({});
    setAdditionalCharge('');
    setRestoreNote('');
    setRestoreInfo(null);
    setRestoreLoad(true);
    try {
      const r = await api.get(`/api/renewal-cancellations/${item.cancellation_id}/restore-info`);
      setRestoreInfo(r.data);
    } catch (e) {
      showToast('error', e?.response?.data?.detail || 'Could not load package items.');
      setRestoreItem(null);
    } finally { setRestoreLoad(false); }
  };

  const closeRestoreModal = () => {
    setRestoreItem(null);
    setRestoreInfo(null);
    setSwaps({});
    setSwapReasons({});
    setAdditionalCharge('');
    setRestoreNote('');
  };

  const setSwap = (pkgItemId, newInvId) => {
    setSwaps(prev => {
      if (!newInvId) {
        const next = { ...prev };
        delete next[pkgItemId];
        return next;
      }
      return { ...prev, [pkgItemId]: newInvId };
    });
    if (!newInvId) {
      setSwapReasons(prev => { const n = { ...prev }; delete n[pkgItemId]; return n; });
    } else {
      // Default reason when a swap is first selected
      setSwapReasons(prev => ({ ...prev, [pkgItemId]: prev[pkgItemId] || 'damaged' }));
    }
  };

  const setSwapReason = (pkgItemId, reason) => {
    setSwapReasons(prev => ({ ...prev, [pkgItemId]: reason }));
  };

  // ── Confirm Restore ──────────────────────────────────────────────────────────

  const confirmRestore = async () => {
    if (!restoreItem || !restoreInfo) return;
    setRestoring(true);
    try {
      const replacements = Object.entries(swaps).map(([pkg_item_id, new_inv_id]) => ({
        pkg_item_id: Number(pkg_item_id),
        new_inv_id:  Number(new_inv_id),
        reason:      swapReasons[pkg_item_id] || 'other',
      }));
      const res = await api.post(
        `/api/renewal-cancellations/${restoreItem.cancellation_id}/restore`,
        {
          replacements,
          amc_amount:        restoreInfo.amc_amount,
          additional_charge: parseFloat(additionalCharge) || 0,
          notes:             restoreNote.trim() || null,
        }
      );
      await load();
      closeRestoreModal();
      const total = res.data.total_due || res.data.amc_amount;
      showToast('success', `Subscription restored! Credit entry ₹${total.toLocaleString('en-IN')} added for ${restoreInfo.client_name}.`);
    } catch (e) {
      showToast('error', e?.response?.data?.detail || 'Restore failed.');
    } finally { setRestoring(false); }
  };

  // ── Computed ──────────────────────────────────────────────────────────────────

  const activeItems    = useMemo(() => items.filter(i => !i.cancelled), [items]);
  const cancelledItems = useMemo(() => items.filter(i => i.cancelled),  [items]);

  // The admin list is split so dealer-managed clients get their own tab. A dealer's
  // OWN clients are all dealer clients, so for a dealer login there is nothing to
  // split off — everything belongs in their main tabs.
  const regularItems      = useMemo(
    () => (isDealer ? activeItems : activeItems.filter(i => !i.is_dealer_client)),
    [activeItems, isDealer]
  );
  const dealerClientItems = useMemo(
    () => (isDealer ? [] : activeItems.filter(i => i.is_dealer_client)),
    [activeItems, isDealer]
  );

  const summary = useMemo(() => {
    const overdue  = regularItems.filter(i => i.status === 'overdue');
    const due_soon = regularItems.filter(i => i.status === 'due_soon');
    return {
      overdue_count:   overdue.length,
      due_soon_count:  due_soon.length,
      upcoming_count:  regularItems.filter(i => i.status === 'upcoming').length,
      cancelled_count: cancelledItems.length,
      total_amc:       regularItems.reduce((s, i) => s + (i.amc_amount || 0), 0),
      overdue_amc:     overdue.reduce((s, i)     => s + (i.amc_amount || 0), 0),
      due_soon_amc:    due_soon.reduce((s, i)    => s + (i.amc_amount || 0), 0),
    };
  }, [regularItems, cancelledItems]);

  const pendingRequests = useMemo(() => requests.filter(r => r.status === 'pending'), [requests]);

  // Completed requests leave this tab — they live in Renewal History now.
  const openRequests = useMemo(() => requests.filter(r => r.status !== 'completed'), [requests]);

  const tabCount = useMemo(() => {
    const c = {
      all:             regularItems.length,
      cancelled:       cancelledItems.length,
      dealer_clients:  dealerClientItems.length,
      dealer_requests: pendingRequests.length,
    };
    regularItems.forEach(i => { c[i.status] = (c[i.status] || 0) + 1; });
    return c;
  }, [regularItems, dealerClientItems, cancelledItems, pendingRequests]);

  const parseDMY = (str) => {
    if (!str) return null;
    const [d, m, y] = str.split('-');
    return new Date(Number(y), Number(m) - 1, Number(d));
  };

  // Everything in the current tab that matches the tab + search, IGNORING the date range.
  // `filtered` then applies the date range on top, so the difference tells us exactly
  // how many rows the date filter is hiding (used to explain an empty table).
  const tabMatched = useMemo(() => {
    const isCancTab   = tab === 'cancelled';
    const isDealTab   = tab === 'dealer_clients';
    const pool = isCancTab ? cancelledItems : isDealTab ? dealerClientItems : regularItems;
    const q = search.toLowerCase();

    return pool.filter(i => {
      if (!isCancTab && !isDealTab && tab !== 'all' && i.status !== tab) return false;
      if (!q) return true;
      return [i.client_name, i.client_ref, i.package_ref, i.category_name, i.type_name, i.serial_number, i.installer_name, i.dealer_name]
        .some(v => v?.toLowerCase().includes(q));
    });
  }, [tab, search, regularItems, dealerClientItems, cancelledItems]);

  const filtered = useMemo(() => {
    const isCancTab = tab === 'cancelled';

    const today = new Date();
    const ty = today.getFullYear(), tm = today.getMonth();
    const ly = tm === 0 ? ty - 1 : ty,  lm = tm === 0 ? 11 : tm - 1;
    const ny = tm === 11 ? ty + 1 : ty, nm = tm === 11 ? 0  : tm + 1;
    const fromDate = customFrom ? new Date(customFrom) : null;
    const toDate   = customTo   ? new Date(customTo + 'T23:59:59') : null;

    return tabMatched
      .filter(i => {
        if (isCancTab || dateFilter === 'all') return true;
        const due = parseDMY(i.due_date);
        if (!due) return false;
        if (dateFilter === 'this_month') return due.getFullYear() === ty && due.getMonth() === tm;
        if (dateFilter === 'last_month') return due.getFullYear() === ly && due.getMonth() === lm;
        if (dateFilter === 'next_month') return due.getFullYear() === ny && due.getMonth() === nm;
        if (dateFilter === 'custom') {
          if (fromDate && due < fromDate) return false;
          if (toDate   && due > toDate)   return false;
        }
        return true;
      })
      .sort((a, b) => a.days_remaining - b.days_remaining);
  }, [tabMatched, tab, dateFilter, customFrom, customTo]);

  // How many rows the date filter alone is suppressing
  const hiddenByDate = tabMatched.length - filtered.length;

  // Swap cost total — only charge when warranty_applicable AND client warranty has expired
  const swapCost = useMemo(() => {
    if (!restoreInfo) return 0;
    return Object.entries(swaps).reduce((sum, [pkgItemId, invId]) => {
      const it = restoreInfo.items.find(i => i.pkg_item_id === Number(pkgItemId));
      if (!it) return sum;
      const shouldCharge = it.warranty_applicable && it.client_warranty_end && warrantyExpired(it.client_warranty_end);
      if (!shouldCharge) return sum;
      const rep = it.available_replacements.find(r => r.id === Number(invId));
      return sum + (rep?.amount || 0);
    }, 0);
  }, [swaps, restoreInfo]);

  const renewSwapCost = useMemo(() => {
    if (!renewInfo) return 0;
    return Object.entries(renewSwaps).reduce((sum, [pkgItemId, invId]) => {
      const it = renewInfo.items.find(i => i.pkg_item_id === Number(pkgItemId));
      if (!it) return sum;
      const shouldCharge = it.warranty_applicable && it.client_warranty_end && warrantyExpired(it.client_warranty_end);
      if (!shouldCharge) return sum;
      const rep = it.available_replacements.find(r => r.id === Number(invId));
      return sum + (rep?.amount || 0);
    }, 0);
  }, [renewSwaps, renewInfo]);

  const isCancelledTab = tab === 'cancelled';
  const isDealerTab    = tab === 'dealer_clients';
  const isReqTab       = tab === 'dealer_requests';

  return (
    <LayoutComp title="Renewal List">

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: '20px', right: '24px', zIndex: 5000,
          background: toast.type === 'success' ? '#D1FAE5' : '#FEE2E2',
          color:      toast.type === 'success' ? '#065F46' : '#991B1B',
          border:     `1px solid ${toast.type === 'success' ? '#6EE7B7' : '#FCA5A5'}`,
          borderRadius: '12px', padding: '14px 22px', fontSize: '13px', fontWeight: 600,
          boxShadow: '0 6px 24px rgba(0,0,0,0.15)', maxWidth: '400px', lineHeight: 1.5,
        }}>
          {toast.type === 'success' ? '✓' : '⚠'} {toast.text}
        </div>
      )}

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(185px, 1fr))', gap: '14px', marginBottom: '22px' }}>
        <SummCard icon="🚨" label="Overdue"        count={summary.overdue_count}  amc={summary.overdue_amc}  dot="#EF4444" bg="#FEE2E2" color="#991B1B" sub="need immediate attention" />
        <SummCard icon="⚠️" label="Due This Month" count={summary.due_soon_count} amc={summary.due_soon_amc} dot="#F59E0B" bg="#FEF3C7" color="#92400E" sub="due within 30 days" />
        <SummCard icon="📅" label="Upcoming"       count={summary.upcoming_count} amc={null}                 dot="#10B981" bg="#D1FAE5" color="#065F46" sub="renewals beyond 30 days" />
        <SummCard icon="💰" label="Total AMC"      count={null}                   amc={summary.total_amc}    dot="#8B5CF6" bg="#EDE9FE" color="#5B21B6" sub="across active renewals" />
        <SummCard icon="🚫" label="Cancelled"      count={summary.cancelled_count} amc={null}                dot="#6B7280" bg="#F3F4F6" color="#374151" sub="subscriptions cancelled" />
        {isDealer && (
          <SummCard icon="👛" label="Wallet Balance" count={null} amc={wallet ?? 0} dot="#0D9488" bg="#F0FDFA" color="#0F766E" sub="renewal requests debit this" />
        )}
        {!isDealer && pendingRequests.length > 0 && (
          <SummCard icon="📨" label="Dealer Requests" count={pendingRequests.length} amc={null} dot="#F59E0B" bg="#FEF3C7" color="#92400E" sub="awaiting your approval" />
        )}
      </div>

      {/* Table Card */}
      <div style={{ background: C.white, borderRadius: '14px', border: `1px solid ${C.border}`, overflow: 'hidden' }}>

        {/* Tabs + Search */}
        <div style={{ padding: '16px 22px', borderBottom: `1px solid ${C.border}`, display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: '4px', background: '#F3F4F6', borderRadius: '10px', padding: '4px' }}>
            {TABS.filter(t => !(isDealer && ADMIN_ONLY_TABS.includes(t.key))).map(t => {
              const sc     = STATUS_CFG[t.key];
              const active = tab === t.key;
              const isCT   = t.key === 'cancelled';
              const isDT   = t.key === 'dealer_clients';
              const isRT   = t.key === 'dealer_requests';
              const isHT   = t.key === 'history' || t.key === 'history_dealer';
              const solid  = isCT ? '#374151' : isDT ? '#0F766E'
                           : isRT ? '#B45309' : isHT ? '#5B21B6' : null;
              return (
                <button key={t.key} onClick={() => setTab(t.key)} style={{
                  padding: '6px 14px', borderRadius: '7px', fontSize: '12px', fontWeight: 700,
                  border: 'none', cursor: 'pointer', transition: 'all 0.15s',
                  background: active ? (solid || C.white) : 'transparent',
                  color:      active ? (solid ? '#fff' : C.textMain) : C.textSub,
                  boxShadow:  active ? '0 1px 4px rgba(0,0,0,0.12)' : 'none',
                }}>
                  {t.label}
                  {(tabCount[t.key] || 0) > 0 && (
                    <span style={{
                      marginLeft: '6px', padding: '1px 7px', borderRadius: '20px', fontSize: '10px',
                      background: active ? (sc ? sc.bg : solid ? 'rgba(255,255,255,0.28)' : '#E5E7EB') : (isRT ? '#FEF3C7' : '#E5E7EB'),
                      color:      active ? (sc ? sc.color : solid ? '#fff' : C.textSub) : (isRT ? '#92400E' : C.textSub),
                      fontWeight: 800,
                    }}>
                      {tabCount[t.key] || 0}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <input
            placeholder="Search client, category, serial, installer…"
            value={search} onChange={e => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: '200px', padding: '8px 14px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', outline: 'none' }}
          />
          {search && (
            <button onClick={() => setSearch('')} style={{ padding: '8px 14px', background: C.primaryBg, color: C.textSub, border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
              Clear
            </button>
          )}
          <button onClick={load} title="Refresh" style={{ padding: '8px 12px', background: C.primaryBg, border: `1px solid ${C.border}`, borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}>↻</button>

          {/* Bulk WhatsApp reminders — admin only, on the actionable renewal tabs */}
          {!isDealer && !isReqTab && !isCancelledTab && filtered.length > 0 && (
            <button onClick={() => openReminder(filtered)} title="Send WhatsApp renewal reminders to everyone in this view"
              style={{ padding: '8px 14px', background: 'linear-gradient(135deg,#25D366,#128C7E)', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: 700, whiteSpace: 'nowrap', boxShadow: '0 2px 6px rgba(18,140,126,0.3)' }}>
              💬 Send Reminders ({filtered.length})
            </button>
          )}

          <span style={{ fontSize: '12px', color: C.textSub, whiteSpace: 'nowrap' }}>
            {isReqTab ? openRequests.length : filtered.length} items
          </span>
        </div>

        {/* Date filter row */}
        {!isCancelledTab && !isReqTab && (
          <div style={{ padding: '10px 22px', borderBottom: `1px solid ${C.border}`, background: '#FAFAFA', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '11px', fontWeight: 700, color: C.textSub, textTransform: 'uppercase', letterSpacing: '0.5px', marginRight: '2px' }}>Due Date:</span>
            {[
              { key: 'all',        label: 'All'        },
              { key: 'last_month', label: 'Last Month' },
              { key: 'this_month', label: 'This Month' },
              { key: 'next_month', label: 'Next Month' },
              { key: 'custom',     label: 'Custom'     },
            ].map(opt => (
              <button key={opt.key}
                onClick={() => { setDateFilter(opt.key); if (opt.key !== 'custom') { setCustomFrom(''); setCustomTo(''); } }}
                style={{
                  padding: '5px 13px', borderRadius: '20px', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                  background: dateFilter === opt.key ? C.primary : C.white,
                  color:      dateFilter === opt.key ? '#fff'    : C.textSub,
                  border:     `1.5px solid ${dateFilter === opt.key ? C.primary : C.border}`,
                  transition: 'all 0.15s',
                }}>
                {opt.label}
              </button>
            ))}
            {dateFilter === 'custom' && (
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginLeft: '4px' }}>
                <span style={{ fontSize: '11px', color: C.textSub }}>From</span>
                <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
                  style={{ padding: '5px 10px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '12px', outline: 'none' }} />
                <span style={{ fontSize: '11px', color: C.textSub }}>To</span>
                <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
                  style={{ padding: '5px 10px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '12px', outline: 'none' }} />
                {(customFrom || customTo) && (
                  <button onClick={() => { setCustomFrom(''); setCustomTo(''); }}
                    style={{ padding: '4px 10px', borderRadius: '8px', fontSize: '11px', fontWeight: 600, background: '#FEE2E2', color: '#991B1B', border: '1px solid #FECACA', cursor: 'pointer' }}>
                    Clear
                  </button>
                )}
              </div>
            )}

            {/* Live hint: rows exist but this date range is hiding them */}
            {hiddenByDate > 0 && (
              <button
                onClick={() => { setDateFilter('all'); setCustomFrom(''); setCustomTo(''); }}
                title="Show renewals with any due date"
                style={{
                  marginLeft: 'auto', padding: '5px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: 700,
                  background: '#FEF3C7', color: '#92400E', border: '1.5px solid #FDE68A', cursor: 'pointer', whiteSpace: 'nowrap',
                }}>
                🗓️ {hiddenByDate} hidden by this date range — show all
              </button>
            )}
          </div>
        )}

        {/* Cancelled tab notice */}
        {isCancelledTab && (
          <div style={{ padding: '10px 22px', background: '#F9FAFB', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '13px' }}>🚫</span>
            <span style={{ fontSize: '12px', color: '#374151', fontWeight: 500 }}>
              Cancelled subscriptions. Click <strong>↩ Restore</strong> to review items, optionally swap any IMEI, then restore to Credit List.
            </span>
          </div>
        )}

        {/* Dealer clients tab notice */}
        {isDealerTab && (
          <div style={{ padding: '10px 22px', background: '#F0FDFA', borderBottom: `1px solid #99F6E4`, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '13px' }}>🏪</span>
            <span style={{ fontSize: '12px', color: '#0F766E', fontWeight: 500 }}>
              Renewals for dealer-managed clients. <strong>AMC amounts are editable</strong> — click ✎ on any row to set a custom rate for that client.
            </span>
          </div>
        )}

        {/* Dealer renewal requests tab notice */}
        {isReqTab && (
          <div style={{ padding: '10px 22px', background: '#FFFBEB', borderBottom: '1px solid #FDE68A', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '13px' }}>📨</span>
            <span style={{ fontSize: '12px', color: '#92400E', fontWeight: 500 }}>
              Open requests raised by dealers. The dealer's wallet was <strong>already debited</strong> when the request was raised.
              Mark <strong>✓ Completed</strong> to apply the renewal — the record then moves to <strong>Renewal History</strong>.
              <strong> ✕ Reject</strong> refunds their wallet.
            </span>
          </div>
        )}

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isDealerTab ? '1040px' : '960px' }}>
            <thead>
              <tr style={{ background: isCancelledTab ? '#F3F4F6' : isDealerTab ? '#F0FDFA' : isReqTab ? '#FFFBEB' : C.primaryBg }}>
                {(isCancelledTab
                  ? ['Client', 'Package', 'Category / Type', 'Serial No', 'AMC (₹)', 'Due Date', 'Cancelled On', 'Reason', 'Action']
                  : isReqTab
                    ? ['Request', 'Dealer', 'Client', 'Category / Type', 'Serial No', 'Dealer Charge (₹)', 'Client AMC (₹)', 'Raised On', 'Status', 'Actions']
                  : isDealerTab
                    ? ['Client', 'Dealer', 'Package', 'Category / Type', 'Serial No', 'AMC (₹)', 'Duration', 'Due Date', 'Days Left', 'Status', 'Actions']
                    : ['Client', 'Package', 'Category / Type', 'Serial No', 'Installer', 'AMC (₹)', 'Duration', 'Install Date', 'Due Date', 'Days Left', 'Status', 'Actions']
                ).map(h => (
                  <th key={h} style={{
                    padding: '11px 14px', fontSize: '11px', fontWeight: 700,
                    color: isCancelledTab ? '#6B7280' : isDealerTab ? '#0F766E' : isReqTab ? '#92400E' : C.textSub,
                    textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.5px',
                    borderBottom: `2px solid ${isCancelledTab ? '#E5E7EB' : isDealerTab ? '#99F6E4' : isReqTab ? '#FDE68A' : C.border}`, whiteSpace: 'nowrap',
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={12} style={{ textAlign: 'center', padding: '70px', color: C.textSub, fontSize: '13px' }}>Loading renewal list…</td></tr>
              ) : isReqTab ? (
                /* ── Dealer renewal requests (open only — completed move to History) ── */
                openRequests.length === 0 ? (
                  <tr><td colSpan={10} style={{ textAlign: 'center', padding: '70px' }}>
                    <div style={{ fontSize: '40px', marginBottom: '14px' }}>📨</div>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: C.textMain, marginBottom: '5px' }}>No open dealer requests</div>
                    <div style={{ fontSize: '12px', color: C.textSub }}>
                      When a dealer raises a renewal request it appears here for approval.
                      {history.filter(h => h.source === 'dealer_request').length > 0 && (
                        <> Completed ones are in{' '}
                          <button onClick={() => setTab('history_dealer')} style={{ background: 'none', border: 'none', padding: 0, color: '#5B21B6', fontWeight: 700, fontSize: '12px', cursor: 'pointer', textDecoration: 'underline' }}>
                            📜 Renewal History
                          </button>.
                        </>
                      )}
                    </div>
                  </td></tr>
                ) : openRequests.map((r, i) => {
                  const rc = REQ_CFG[r.status] || REQ_CFG.pending;
                  return (
                    <tr key={r.id} style={{ background: i % 2 === 0 ? C.white : '#FCFCFC', borderBottom: `1px solid ${rc.border}` }}>
                      <td style={{ padding: '12px 14px', verticalAlign: 'middle' }}>
                        <div style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 800, color: '#B45309' }}>{r.request_id}</div>
                        {r.mapping_ref && <div style={{ fontFamily: 'monospace', fontSize: '10px', color: C.textSub }}>{r.mapping_ref}</div>}
                      </td>
                      <td style={{ padding: '12px 14px', verticalAlign: 'middle' }}>
                        <span style={{ fontSize: '12px', fontWeight: 600, color: '#0F766E', background: '#F0FDFA', padding: '3px 10px', borderRadius: '8px', whiteSpace: 'nowrap' }}>
                          🏪 {r.dealer_name || '—'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 14px', verticalAlign: 'middle' }}>
                        <div style={{ fontWeight: 700, fontSize: '13px', color: C.textMain }}>{r.client_name}</div>
                        <div style={{ fontSize: '10px', color: C.textSub }}>{r.client_ref}</div>
                      </td>
                      <td style={{ padding: '12px 14px', verticalAlign: 'middle' }}>
                        <div style={{ fontWeight: 600, fontSize: '13px', color: C.textMain }}>{r.category_name}</div>
                        {r.type_name && (
                          <span style={{ fontSize: '11px', background: C.primaryBg, color: C.primaryDeep, padding: '1px 8px', borderRadius: '10px', fontWeight: 600, display: 'inline-block', marginTop: '2px' }}>
                            {r.type_name}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '12px 14px', verticalAlign: 'middle' }}>
                        <div style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 600, color: C.textMain }}>{r.serial_number || r.item_id || '—'}</div>
                        {r.ref_number && <div style={{ fontFamily: 'monospace', fontSize: '11px', color: C.textSub, marginTop: '1px' }}>Ref: {r.ref_number}</div>}
                      </td>
                      <td style={{ padding: '12px 14px', verticalAlign: 'middle' }}>
                        <div style={{ fontWeight: 800, fontSize: '14px', color: '#B45309' }}>₹{(r.dealer_amount || 0).toLocaleString('en-IN')}</div>
                        <div style={{ fontSize: '10px', color: C.textSub }}>debited from wallet</div>
                      </td>
                      <td style={{ padding: '12px 14px', verticalAlign: 'middle' }}>
                        <span style={{ fontWeight: 700, fontSize: '13px', color: '#7C3AED' }}>₹{(r.amc_amount || 0).toLocaleString('en-IN')}</span>
                      </td>
                      <td style={{ padding: '12px 14px', verticalAlign: 'middle', fontSize: '12px', color: C.textSub, whiteSpace: 'nowrap' }}>
                        {r.created_at}
                        {r.notes && <div style={{ fontSize: '11px', color: '#9CA3AF', fontStyle: 'italic', marginTop: '2px', maxWidth: '160px' }}>"{r.notes}"</div>}
                      </td>
                      <td style={{ padding: '12px 14px', verticalAlign: 'middle' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: rc.bg, color: rc.color, padding: '4px 11px', borderRadius: '20px', fontSize: '11px', fontWeight: 700, whiteSpace: 'nowrap' }}>
                          <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: rc.dot, flexShrink: 0 }} />
                          {rc.label}
                        </span>
                        {r.completed_at && <div style={{ fontSize: '10px', color: C.textSub, marginTop: '3px' }}>{r.completed_at}</div>}
                        {r.admin_notes && <div style={{ fontSize: '10px', color: '#9CA3AF', fontStyle: 'italic', marginTop: '2px', maxWidth: '150px' }}>"{r.admin_notes}"</div>}
                      </td>
                      <td style={{ padding: '12px 14px', verticalAlign: 'middle' }}>
                        {r.status === 'pending' ? (
                          <div style={{ display: 'flex', gap: '6px', flexDirection: 'column' }}>
                            <button onClick={() => { setActionReq({ req: r, mode: 'complete' }); setActionNote(''); }}
                              style={{ padding: '5px 12px', borderRadius: '7px', fontSize: '11px', fontWeight: 700, border: 'none', cursor: 'pointer', whiteSpace: 'nowrap', background: 'linear-gradient(135deg,#10B981,#059669)', color: '#fff', boxShadow: '0 2px 6px rgba(16,185,129,0.3)' }}>
                              ✓ Mark Completed
                            </button>
                            <button onClick={() => { setActionReq({ req: r, mode: 'reject' }); setActionNote(''); }}
                              style={{ padding: '5px 12px', borderRadius: '7px', fontSize: '11px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', background: '#FEE2E2', color: '#991B1B', border: '1px solid #FECACA' }}>
                              ✕ Reject
                            </button>
                          </div>
                        ) : (
                          <span style={{ fontSize: '11px', color: '#9CA3AF' }}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : filtered.length === 0 ? (
                <tr><td colSpan={12} style={{ textAlign: 'center', padding: '60px 24px' }}>
                  <div style={{ fontSize: '40px', marginBottom: '14px' }}>
                    {hiddenByDate > 0 ? '🗓️' : isCancelledTab ? '🚫' : isDealerTab ? '🏪' : '🔄'}
                  </div>

                  {hiddenByDate > 0 ? (
                    /* Rows exist — the date filter is just hiding them */
                    <>
                      <div style={{ fontSize: '14px', fontWeight: 700, color: C.textMain, marginBottom: '5px' }}>
                        Nothing due in this date range
                      </div>
                      <div style={{ fontSize: '12px', color: C.textSub, marginBottom: '16px', lineHeight: 1.6 }}>
                        <strong style={{ color: '#B45309' }}>{hiddenByDate}</strong> renewal{hiddenByDate !== 1 ? 's' : ''} in this tab {hiddenByDate !== 1 ? 'are' : 'is'} hidden by the
                        <strong> {({ this_month: 'This Month', last_month: 'Last Month', next_month: 'Next Month', custom: 'Custom' })[dateFilter] || dateFilter}</strong> due-date filter.
                        <br />Renewals run on a 12-month cycle, so a freshly-cleared client is usually due next year.
                      </div>
                      <button
                        onClick={() => { setDateFilter('all'); setCustomFrom(''); setCustomTo(''); }}
                        style={{ padding: '9px 20px', borderRadius: '8px', fontSize: '13px', fontWeight: 700, border: 'none', cursor: 'pointer', background: C.primary, color: '#fff' }}>
                        🗓️ Show all due dates ({hiddenByDate})
                      </button>
                    </>
                  ) : (
                    <>
                      <div style={{ fontSize: '14px', fontWeight: 700, color: C.textMain, marginBottom: '5px' }}>
                        {isCancelledTab ? 'No cancelled subscriptions' : isDealerTab ? 'No dealer client renewals' : 'No renewal items'}
                      </div>
                      <div style={{ fontSize: '12px', color: C.textSub, lineHeight: 1.6 }}>
                        {isCancelledTab ? 'Use the Cancel button on any renewal row to cancel a subscription.'
                          : isDealerTab ? 'No renewals found for dealer-managed clients.'
                          : items.length === 0 ? 'No paid credit entries found. Mark a client as Paid in the Credit List first.'
                          : 'No items match the current filter.'}
                      </div>
                    </>
                  )}

                  {/* Dealer-client renewals live in their own tab — point the user there */}
                  {!isDealer && !isDealerTab && !isCancelledTab && dealerClientItems.length > 0 && (
                    <div style={{ marginTop: '18px', paddingTop: '16px', borderTop: `1px solid ${C.border}`, fontSize: '12px', color: C.textSub }}>
                      Looking for a dealer's client?{' '}
                      <button
                        onClick={() => setTab('dealer_clients')}
                        style={{ background: 'none', border: 'none', padding: 0, color: '#0F766E', fontWeight: 700, fontSize: '12px', cursor: 'pointer', textDecoration: 'underline' }}>
                        🏪 {dealerClientItems.length} renewal{dealerClientItems.length !== 1 ? 's' : ''} {dealerClientItems.length !== 1 ? 'are' : 'is'} in the Dealer Clients tab
                      </button>
                    </div>
                  )}
                </td></tr>
              ) : isDealerTab ? (
                /* ── Dealer client renewal rows ── */
                filtered.map((item, i) => {
                  const sc  = STATUS_CFG[item.status] || STATUS_CFG.upcoming;
                  const bgs = ROW_BG[item.status]    || ROW_BG.upcoming;
                  const isEditingThis = editingAmc?.uid === item.uid;
                  return (
                    <tr key={item.uid} style={{ background: bgs[i % 2], borderBottom: `1px solid ${sc.border}` }}>
                      {/* Client */}
                      <td style={{ padding: '12px 14px', verticalAlign: 'middle' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
                          <div style={{ width: '32px', height: '32px', borderRadius: '50%', flexShrink: 0, background: 'linear-gradient(135deg,#0D9488,#0F766E)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 800, color: '#fff' }}>
                            {(item.client_name || 'C')[0].toUpperCase()}
                          </div>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: '13px', color: C.textMain }}>{item.client_name}</div>
                            <div style={{ fontSize: '10px', color: C.textSub }}>{item.client_ref}</div>
                          </div>
                        </div>
                      </td>
                      {/* Dealer */}
                      <td style={{ padding: '12px 14px', verticalAlign: 'middle' }}>
                        <span style={{ fontSize: '12px', fontWeight: 600, color: '#0F766E', background: '#F0FDFA', padding: '3px 10px', borderRadius: '8px', whiteSpace: 'nowrap' }}>
                          🏪 {item.dealer_name || '—'}
                        </span>
                      </td>
                      {/* Package */}
                      <td style={{ padding: '12px 14px', verticalAlign: 'middle' }}>
                        <div style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 700, color: '#7C3AED' }}>{item.package_ref}</div>
                        {item.package_name && <div style={{ fontSize: '11px', color: C.textSub }}>{item.package_name}</div>}
                      </td>
                      {/* Category */}
                      <td style={{ padding: '12px 14px', verticalAlign: 'middle' }}>
                        <div style={{ fontWeight: 600, fontSize: '13px', color: C.textMain }}>{item.category_name}</div>
                        {item.type_name && (
                          <span style={{ fontSize: '11px', background: C.primaryBg, color: C.primaryDeep, padding: '1px 8px', borderRadius: '10px', fontWeight: 600, display: 'inline-block', marginTop: '2px' }}>
                            {item.type_name}
                          </span>
                        )}
                      </td>
                      {/* Serial */}
                      <td style={{ padding: '12px 14px', verticalAlign: 'middle' }}>
                        <div style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 600, color: C.textMain }}>{item.serial_number || item.item_id || '—'}</div>
                        {item.ref_number && <div style={{ fontFamily: 'monospace', fontSize: '11px', color: C.textSub, marginTop: '1px' }}>Ref: {item.ref_number}</div>}
                      </td>
                      {/* AMC — editable */}
                      <td style={{ padding: '12px 14px', verticalAlign: 'middle' }} onClick={e => e.stopPropagation()}>
                        {isEditingThis ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <span style={{ fontSize: '13px', fontWeight: 700, color: '#0F766E' }}>₹</span>
                            <input
                              type="number" min="0" step="1"
                              value={editingAmc.value}
                              onChange={e => setEditingAmc(prev => ({ ...prev, value: e.target.value }))}
                              onKeyDown={e => { if (e.key === 'Enter') saveAmc(); if (e.key === 'Escape') setEditingAmc(null); }}
                              autoFocus
                              style={{ width: '80px', padding: '4px 8px', border: '1.5px solid #0D9488', borderRadius: '6px', fontSize: '13px', fontWeight: 700, outline: 'none', color: '#0F766E' }}
                            />
                            <button onClick={saveAmc} disabled={savingAmc} title="Save"
                              style={{ padding: '4px 8px', background: '#0D9488', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>
                              {savingAmc ? '…' : '✓'}
                            </button>
                            <button onClick={() => setEditingAmc(null)} title="Cancel"
                              style={{ padding: '4px 7px', background: '#F3F4F6', color: '#6B7280', border: '1px solid #E5E7EB', borderRadius: '6px', fontSize: '11px', cursor: 'pointer' }}>
                              ✕
                            </button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}
                            onClick={() => setEditingAmc({ uid: item.uid, pkg_item_id: item.pkg_item_id, value: item.amc_amount })}>
                            <span style={{ fontWeight: 800, fontSize: '14px', color: '#7C3AED' }}>₹{(item.amc_amount || 0).toLocaleString('en-IN')}</span>
                            {item.amc_overridden && (
                              <span style={{ fontSize: '9px', background: '#FEF3C7', color: '#92400E', padding: '1px 5px', borderRadius: '4px', fontWeight: 700 }}>custom</span>
                            )}
                            <span style={{ fontSize: '11px', color: '#9CA3AF' }}>✎</span>
                          </div>
                        )}
                        {item.amc_overridden && !isEditingThis && (
                          <div style={{ fontSize: '10px', color: '#9CA3AF', marginTop: '2px' }}>
                            default: ₹{(item.amc_amount_default || 0).toLocaleString('en-IN')}
                          </div>
                        )}
                      </td>
                      {/* Duration */}
                      <td style={{ padding: '12px 14px', verticalAlign: 'middle' }}>
                        <span style={{ background: C.primaryBg, color: C.primaryDeep, padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 600 }}>
                          {item.duration_months} mo
                        </span>
                      </td>
                      {/* Due Date */}
                      <td style={{ padding: '12px 14px', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                        <span style={{ fontSize: '12px', fontWeight: 700, color: sc.color }}>{item.due_date}</span>
                      </td>
                      {/* Days Left */}
                      <td style={{ padding: '12px 14px', verticalAlign: 'middle' }}>
                        <DaysChip days={item.days_remaining} sc={sc} />
                      </td>
                      {/* Status */}
                      <td style={{ padding: '12px 14px', verticalAlign: 'middle' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: sc.bg, color: sc.color, padding: '4px 11px', borderRadius: '20px', fontSize: '11px', fontWeight: 700, whiteSpace: 'nowrap' }}>
                          <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: sc.dot, flexShrink: 0 }} />
                          {sc.label}
                        </span>
                      </td>
                      {/* Actions */}
                      <td style={{ padding: '12px 14px', verticalAlign: 'middle' }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', gap: '6px', flexDirection: 'column' }}>
                          {item.credit_pending ? (
                            <>
                              <button onClick={() => navigate(creditListPath)} style={{ padding: '5px 12px', borderRadius: '7px', fontSize: '11px', fontWeight: 700, border: '1.5px solid #D97706', cursor: 'pointer', whiteSpace: 'nowrap', background: '#FEF3C7', color: '#92400E' }}>
                                💳 Credit Pending
                              </button>
                              <span style={{ fontSize: '10px', fontWeight: 700, color: '#B45309', textAlign: 'center' }}>
                                ₹{(item.credit_balance || 0).toLocaleString('en-IN')} due
                              </span>
                            </>
                          ) : (
                            <>
                              <button onClick={() => openRenewModal(item)} style={{ padding: '5px 12px', borderRadius: '7px', fontSize: '11px', fontWeight: 700, border: 'none', cursor: 'pointer', whiteSpace: 'nowrap', background: 'linear-gradient(135deg,#F59E0B,#D97706)', color: '#fff', boxShadow: '0 2px 6px rgba(245,158,11,0.3)' }}>
                                🔄 Renew Now
                              </button>
                              <div style={{ display: 'flex', gap: '6px' }}>
                                <button onClick={() => openReminder([item])} title="Send WhatsApp reminder" style={{ flex: 1, padding: '5px 10px', borderRadius: '7px', fontSize: '11px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', background: '#DCFCE7', color: '#128C7E', border: '1px solid #86EFAC' }}>
                                  💬 Remind
                                </button>
                                <button onClick={() => { setCancelItem(item); setCancelReason(''); }} style={{ flex: 1, padding: '5px 10px', borderRadius: '7px', fontSize: '11px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', background: '#FEE2E2', color: '#991B1B', border: '1px solid #FECACA' }}>
                                  🚫 Cancel
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : isCancelledTab ? (
                /* ── Cancelled rows ── */
                filtered.map((item, i) => (
                  <tr key={item.uid} style={{ background: i % 2 === 0 ? '#F9FAFB' : '#F3F4F6', borderBottom: '1px solid #E5E7EB', opacity: 0.85 }}>
                    <td style={{ padding: '12px 14px', verticalAlign: 'middle' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
                        <div style={{ width: '30px', height: '30px', borderRadius: '50%', flexShrink: 0, background: '#9CA3AF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 800, color: '#fff' }}>
                          {(item.client_name || 'C')[0].toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: '13px', color: '#374151' }}>{item.client_name}</div>
                          <div style={{ fontSize: '10px', color: '#9CA3AF' }}>{item.client_ref}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '12px 14px', verticalAlign: 'middle' }}>
                      <div style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 700, color: '#9CA3AF' }}>{item.package_ref}</div>
                      {item.package_name && <div style={{ fontSize: '11px', color: '#9CA3AF' }}>{item.package_name}</div>}
                    </td>
                    <td style={{ padding: '12px 14px', verticalAlign: 'middle' }}>
                      <div style={{ fontWeight: 600, fontSize: '13px', color: '#374151' }}>{item.category_name}</div>
                      {item.type_name && <div style={{ fontSize: '11px', color: '#9CA3AF' }}>{item.type_name}</div>}
                    </td>
                    <td style={{ padding: '12px 14px', verticalAlign: 'middle' }}>
                      <div style={{ fontFamily: 'monospace', fontSize: '12px', color: '#6B7280' }}>{item.serial_number || item.item_id || '—'}</div>
                      {item.ref_number && <div style={{ fontFamily: 'monospace', fontSize: '11px', color: '#9CA3AF', marginTop: '1px' }}>Ref: {item.ref_number}</div>}
                    </td>
                    <td style={{ padding: '12px 14px', verticalAlign: 'middle' }}>
                      <span style={{ fontWeight: 800, fontSize: '14px', color: '#9CA3AF', textDecoration: 'line-through' }}>₹{(item.amc_amount || 0).toLocaleString('en-IN')}</span>
                    </td>
                    <td style={{ padding: '12px 14px', verticalAlign: 'middle', fontSize: '12px', color: '#9CA3AF', whiteSpace: 'nowrap' }}>{item.due_date}</td>
                    <td style={{ padding: '12px 14px', verticalAlign: 'middle', fontSize: '12px', color: '#374151', whiteSpace: 'nowrap' }}>{item.cancelled_at || '—'}</td>
                    <td style={{ padding: '12px 14px', verticalAlign: 'middle', fontSize: '12px', color: '#6B7280', maxWidth: '160px' }}>
                      {item.cancel_reason ? <span style={{ fontStyle: 'italic' }}>"{item.cancel_reason}"</span> : <span style={{ color: '#D1D5DB' }}>—</span>}
                    </td>
                    <td style={{ padding: '12px 14px', verticalAlign: 'middle' }}>
                      <button onClick={() => openRestoreModal(item)} style={{ padding: '6px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: 700, background: '#ECFDF5', color: '#065F46', border: '1px solid #6EE7B7', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                        ↩ Restore
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                /* ── Active renewal rows ── */
                filtered.map((item, i) => {
                  const sc    = STATUS_CFG[item.status] || STATUS_CFG.upcoming;
                  const bgs   = ROW_BG[item.status]    || ROW_BG.upcoming;
                  const isExp = expanded === item.uid;
                  return (
                    <React.Fragment key={item.uid}>
                      <tr onClick={() => setExpanded(isExp ? null : item.uid)}
                        style={{ background: bgs[i % 2], borderBottom: `1px solid ${sc.border}`, cursor: 'pointer' }}>
                        <td style={{ padding: '12px 14px', verticalAlign: 'middle' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
                            <div style={{ width: '32px', height: '32px', borderRadius: '50%', flexShrink: 0, background: `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 800, color: '#fff' }}>
                              {(item.client_name || 'C')[0].toUpperCase()}
                            </div>
                            <div>
                              <div style={{ fontWeight: 700, fontSize: '13px', color: C.textMain }}>{item.client_name}</div>
                              <div style={{ fontSize: '10px', color: C.textSub }}>{item.client_ref}</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: '12px 14px', verticalAlign: 'middle' }}>
                          <div style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 700, color: '#7C3AED' }}>{item.package_ref}</div>
                          {item.package_name && <div style={{ fontSize: '11px', color: C.textSub }}>{item.package_name}</div>}
                        </td>
                        <td style={{ padding: '12px 14px', verticalAlign: 'middle' }}>
                          <div style={{ fontWeight: 600, fontSize: '13px', color: C.textMain }}>{item.category_name}</div>
                          {item.type_name && (
                            <span style={{ fontSize: '11px', background: C.primaryBg, color: C.primaryDeep, padding: '1px 8px', borderRadius: '10px', fontWeight: 600, display: 'inline-block', marginTop: '2px' }}>
                              {item.type_name}
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '12px 14px', verticalAlign: 'middle' }}>
                          <div style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 600, color: C.textMain }}>
                            {item.serial_number || item.item_id || '—'}
                          </div>
                          {item.ref_number && <div style={{ fontFamily: 'monospace', fontSize: '11px', color: C.textSub, marginTop: '1px' }}>Ref: {item.ref_number}</div>}
                        </td>
                        <td style={{ padding: '12px 14px', verticalAlign: 'middle' }}>
                          <div style={{ fontSize: '13px', color: C.textMain }}>{item.installer_name}</div>
                          <div style={{ fontSize: '10px', color: C.textSub }}>@{item.installer_user}</div>
                        </td>
                        <td style={{ padding: '12px 14px', verticalAlign: 'middle' }}>
                          <span style={{ fontWeight: 800, fontSize: '14px', color: '#7C3AED' }}>₹{(item.amc_amount || 0).toLocaleString('en-IN')}</span>
                        </td>
                        <td style={{ padding: '12px 14px', verticalAlign: 'middle' }}>
                          <span style={{ background: C.primaryBg, color: C.primaryDeep, padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 600 }}>
                            {item.duration_months} mo
                          </span>
                        </td>
                        <td style={{ padding: '12px 14px', verticalAlign: 'middle', fontSize: '12px', color: C.textSub, whiteSpace: 'nowrap' }}>{item.install_date}</td>
                        <td style={{ padding: '12px 14px', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                          <span style={{ fontSize: '12px', fontWeight: 700, color: sc.color }}>{item.due_date}</span>
                          {item.renewed_times > 0 && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '3px' }}>
                              <span style={{ fontSize: '9px', fontWeight: 800, background: '#D1FAE5', color: '#065F46', padding: '1px 6px', borderRadius: '10px' }}>
                                ↻ renewed {item.renewed_times}×
                              </span>
                              <span style={{ fontSize: '9px', color: C.textSub }}>+{item.renewed_months}mo</span>
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '12px 14px', verticalAlign: 'middle' }}>
                          <DaysChip days={item.days_remaining} sc={sc} />
                        </td>
                        <td style={{ padding: '12px 14px', verticalAlign: 'middle' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: sc.bg, color: sc.color, padding: '4px 11px', borderRadius: '20px', fontSize: '11px', fontWeight: 700, whiteSpace: 'nowrap' }}>
                            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: sc.dot, flexShrink: 0 }} />
                            {sc.label}
                          </span>
                        </td>
                        <td style={{ padding: '12px 14px', verticalAlign: 'middle' }} onClick={e => e.stopPropagation()}>
                          <div style={{ display: 'flex', gap: '6px', flexDirection: 'column' }}>
                            {item.credit_pending ? (
                              <>
                                <button
                                  onClick={() => navigate(creditListPath)}
                                  style={{
                                    padding: '5px 12px', borderRadius: '7px', fontSize: '11px',
                                    fontWeight: 700, border: '1.5px solid #D97706', cursor: 'pointer',
                                    whiteSpace: 'nowrap', background: '#FEF3C7', color: '#92400E',
                                  }}
                                >
                                  💳 Credit Pending
                                </button>
                                <span style={{ fontSize: '10px', fontWeight: 700, color: '#B45309', textAlign: 'center' }}>
                                  ₹{(item.credit_balance || 0).toLocaleString('en-IN')} due
                                </span>
                              </>
                            ) : isDealer ? (
                              /* Dealer cannot renew directly — they either request a renewal
                                 from admin, or cancel the client's subscription outright. */
                              item.renewal_request_status === 'pending' ? (
                                <>
                                  <span style={{ padding: '5px 12px', borderRadius: '7px', fontSize: '11px', fontWeight: 700, whiteSpace: 'nowrap', background: '#FEF3C7', color: '#92400E', border: '1px solid #FDE68A', textAlign: 'center' }}>
                                    ⏳ Request Pending
                                  </span>
                                  <span style={{ fontSize: '10px', fontWeight: 700, color: '#B45309', textAlign: 'center' }}>
                                    {item.renewal_request_id} · ₹{(item.renewal_request_amount || 0).toLocaleString('en-IN')} debited
                                  </span>
                                </>
                              ) : (
                                <>
                                  <button onClick={() => { setReqItem(item); setReqNote(''); }} style={{ padding: '5px 12px', borderRadius: '7px', fontSize: '11px', fontWeight: 700, border: 'none', cursor: 'pointer', whiteSpace: 'nowrap', background: 'linear-gradient(135deg,#F59E0B,#D97706)', color: '#fff', boxShadow: '0 2px 6px rgba(245,158,11,0.3)' }}>
                                    📨 Request Renewal
                                  </button>
                                  <button onClick={() => { setCancelItem(item); setCancelReason(''); }} style={{ padding: '5px 12px', borderRadius: '7px', fontSize: '11px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', background: '#FEE2E2', color: '#991B1B', border: '1px solid #FECACA' }}>
                                    🚫 Cancel Subscription
                                  </button>
                                  {item.renewal_request_status === 'completed' && (
                                    <span style={{ fontSize: '10px', fontWeight: 700, color: '#065F46', textAlign: 'center' }}>
                                      ✓ {item.renewal_request_id} completed
                                    </span>
                                  )}
                                  {item.renewal_request_status === 'rejected' && (
                                    <span style={{ fontSize: '10px', fontWeight: 700, color: '#991B1B', textAlign: 'center' }}>
                                      ✕ {item.renewal_request_id} rejected · refunded
                                    </span>
                                  )}
                                </>
                              )
                            ) : (
                              <>
                                <button onClick={() => openRenewModal(item)} style={{ padding: '5px 12px', borderRadius: '7px', fontSize: '11px', fontWeight: 700, border: 'none', cursor: 'pointer', whiteSpace: 'nowrap', background: 'linear-gradient(135deg, #F59E0B, #D97706)', color: '#fff', boxShadow: '0 2px 6px rgba(245,158,11,0.3)' }}>
                                  🔄 Renew Now
                                </button>
                                <div style={{ display: 'flex', gap: '6px' }}>
                                  <button onClick={() => openReminder([item])} title="Send WhatsApp reminder" style={{ flex: 1, padding: '5px 10px', borderRadius: '7px', fontSize: '11px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', background: '#DCFCE7', color: '#128C7E', border: '1px solid #86EFAC' }}>
                                    💬 Remind
                                  </button>
                                  <button onClick={() => { setCancelItem(item); setCancelReason(''); }} style={{ flex: 1, padding: '5px 10px', borderRadius: '7px', fontSize: '11px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', background: '#FEE2E2', color: '#991B1B', border: '1px solid #FECACA' }}>
                                    🚫 Cancel
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>

                      {isExp && (
                        <tr style={{ background: '#F8F9FF', borderBottom: `2px solid ${sc.border}` }}>
                          <td colSpan={12} style={{ padding: 0 }}>
                            <div style={{ padding: '16px 22px', display: 'flex', gap: '28px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                              <DetailGroup title="Client Info">
                                <DetailItem label="Name"    value={item.client_name} />
                                <DetailItem label="ID"      value={item.client_ref} mono />
                                {item.client_contact && <DetailItem label="Contact" value={item.client_contact} />}
                                {item.client_email   && <DetailItem label="Email"   value={item.client_email} />}
                              </DetailGroup>
                              <DetailGroup title="Renewal Item">
                                <DetailItem label="Item ID"  value={item.item_id} mono />
                                <DetailItem label="Serial"   value={item.serial_number || '—'} mono />
                                {item.ref_number && <DetailItem label="Ref No." value={item.ref_number} mono />}
                                <DetailItem label="Category" value={item.category_name} />
                                <DetailItem label="Type"     value={item.type_name || '—'} />
                              </DetailGroup>
                              <DetailGroup title="Renewal Schedule">
                                <DetailItem label="Mapping ID"   value={item.mapping_id} mono />
                                <DetailItem label="Package"      value={item.package_ref} mono />
                                <DetailItem label="Installed On" value={item.install_date} />
                                <DetailItem label="Renewal Due"  value={item.due_date} highlight={sc.color} />
                                <DetailItem label="Duration"     value={`${item.duration_months} months`} />
                              </DetailGroup>
                              <div style={{ marginLeft: 'auto', background: sc.bg, border: `1.5px solid ${sc.border}`, borderRadius: '12px', padding: '16px 24px', textAlign: 'center', minWidth: '160px' }}>
                                <div style={{ fontSize: '11px', color: sc.color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>AMC Due</div>
                                <div style={{ fontSize: '26px', fontWeight: 900, color: sc.color }}>₹{(item.amc_amount || 0).toLocaleString('en-IN')}</div>
                                <div style={{ fontSize: '11px', color: sc.color, marginTop: '6px', fontWeight: 600 }}>
                                  {item.days_remaining < 0 ? `${Math.abs(item.days_remaining)} days overdue` : item.days_remaining === 0 ? 'Due today!' : `in ${item.days_remaining} days`}
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {!isReqTab && filtered.length > 0 && (
          <div style={{ padding: '12px 22px', borderTop: `1px solid ${isDealerTab ? '#99F6E4' : C.border}`, display: 'flex', gap: '24px', flexWrap: 'wrap', alignItems: 'center', background: isDealerTab ? '#F0FDFA' : '#FAFAFA' }}>
            <span style={{ fontSize: '12px', color: isDealerTab ? '#0F766E' : C.textSub, fontWeight: 600 }}>SHOWING {filtered.length} ITEMS</span>
            {!isCancelledTab && (
              <>
                <span style={{ fontSize: '12px', color: C.textSub }}>|</span>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  <span style={{ fontSize: '12px', color: C.textSub }}>Total AMC:</span>
                  <span style={{ fontSize: '14px', fontWeight: 800, color: isDealerTab ? '#0F766E' : '#7C3AED' }}>
                    ₹{filtered.reduce((s, i) => s + (i.amc_amount || 0), 0).toLocaleString('en-IN')}
                  </span>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════════════
          RENEW NOW MODAL — items + IMEI swap + credit preview
      ══════════════════════════════════════════════════ */}
      {renewItem && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(14,10,3,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '16px' }}>
          <div style={{ background: '#fff', borderRadius: '18px', width: '100%', maxWidth: '680px', maxHeight: '93vh', display: 'flex', flexDirection: 'column', boxShadow: '0 30px 90px rgba(0,0,0,0.35)', border: '1px solid #FDE68A' }}>

            {/* Header */}
            <div style={{ padding: '18px 24px', borderBottom: '1px solid #FDE68A', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#FFFBEB', borderRadius: '18px 18px 0 0', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'linear-gradient(135deg,#F59E0B,#D97706)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', color: '#fff', fontWeight: 800 }}>🔄</div>
                <div>
                  <div style={{ fontSize: '15px', fontWeight: 800, color: '#78350F' }}>Renew Subscription</div>
                  <div style={{ fontSize: '11px', color: '#D97706', fontWeight: 600 }}>
                    {renewInfo ? `${renewInfo.client_name} · ${renewInfo.package_ref}` : renewItem ? `${renewItem.client_name} · ${renewItem.package_ref}` : 'Loading…'}
                  </div>
                </div>
              </div>
              <button onClick={closeRenewModal} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#6B7280' }}>✕</button>
            </div>

            {/* Body (scrollable) */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>

              {renewLoad && (
                <div style={{ textAlign: 'center', padding: '60px', color: '#6B7280', fontSize: '13px' }}>
                  <div style={{ fontSize: '28px', marginBottom: '10px' }}>⏳</div>
                  Loading package items…
                </div>
              )}

              {!renewLoad && renewInfo && (
                <>
                  {/* Client + AMC */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '12px', marginBottom: '20px' }}>
                    <div style={{ background: '#F9FAFB', borderRadius: '10px', padding: '14px 16px' }}>
                      <div style={{ fontSize: '10px', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>Client</div>
                      <div style={{ fontSize: '15px', fontWeight: 800, color: '#111' }}>{renewInfo.client_name}</div>
                      <div style={{ fontSize: '11px', color: '#6B7280', fontFamily: 'monospace', marginTop: '2px' }}>{renewInfo.client_ref}</div>
                    </div>
                    <div style={{ background: '#FEF3C7', borderRadius: '10px', padding: '14px 22px', textAlign: 'center', minWidth: '140px' }}>
                      <div style={{ fontSize: '10px', fontWeight: 700, color: '#D97706', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>AMC</div>
                      <div style={{ fontSize: '26px', fontWeight: 900, color: '#92400E' }}>₹{(renewItem.amc_amount || 0).toLocaleString('en-IN')}</div>
                    </div>
                  </div>

                  {/* Items heading */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 700, color: C.textMain, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Package Items ({renewInfo.items.length})
                    </div>
                    <div style={{ fontSize: '11px', color: C.textSub }}>Optionally replace any IMEI with an in-stock unit</div>
                  </div>

                  {/* Item cards */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
                    {renewInfo.items.map(it => {
                      const selectedSwap = renewSwaps[it.pkg_item_id] ? Number(renewSwaps[it.pkg_item_id]) : null;
                      const swapRep      = selectedSwap ? it.available_replacements.find(r => r.id === selectedSwap) : null;
                      const hasSubs      = it.available_replacements.length > 0;
                      const currentReason = renewSwapReasons[it.pkg_item_id] || 'damaged';
                      const reasonLabel   = REPLACE_REASONS.find(r => r.value === currentReason)?.label || currentReason;

                      return (
                        <div key={it.pkg_item_id} style={{
                          border: `1.5px solid ${it.is_renewal_item ? '#FDE68A' : C.border}`,
                          borderRadius: '12px', overflow: 'hidden',
                          background: it.is_renewal_item ? '#FFFBEB' : '#FAFAFA',
                        }}>
                          {/* Item header row */}
                          <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: it.is_renewal_item ? '#FEF3C7' : '#E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>📦</div>
                              <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '7px', flexWrap: 'wrap' }}>
                                  <span style={{ fontWeight: 700, fontSize: '13px', color: '#111' }}>{it.category_name}</span>
                                  {it.type_name && (
                                    <span style={{ fontSize: '11px', background: C.primaryBg, color: C.primaryDeep, padding: '1px 7px', borderRadius: '10px', fontWeight: 600 }}>
                                      {it.type_name}
                                    </span>
                                  )}
                                  {it.is_renewal_item && (
                                    <span style={{ fontSize: '10px', background: '#FEF3C7', color: '#92400E', padding: '1px 7px', borderRadius: '10px', fontWeight: 700 }}>
                                      RENEWAL ITEM
                                    </span>
                                  )}
                                  {it.warranty_applicable && (
                                    <WarrantyBadgeMini dateStr={it.client_warranty_end} />
                                  )}
                                </div>
                                <div style={{ display: 'flex', gap: '14px', marginTop: '3px', flexWrap: 'wrap' }}>
                                  <span style={{ fontSize: '11px', color: '#6B7280', fontFamily: 'monospace' }}>{it.item_id}</span>
                                  {it.serial_number && (
                                    <span style={{ fontSize: '11px', color: '#6B7280' }}>
                                      SN: <span style={{ fontFamily: 'monospace', fontWeight: 600, color: '#374151' }}>{it.serial_number}</span>
                                    </span>
                                  )}
                                  {it.ref_number && (
                                    <span style={{ fontSize: '11px', color: '#6B7280' }}>
                                      Ref: <span style={{ fontFamily: 'monospace', fontWeight: 600, color: '#374151' }}>{it.ref_number}</span>
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            {it.amount > 0 && (
                              <div style={{ textAlign: 'right' }}>
                                <div style={{ fontSize: '10px', color: '#9CA3AF', fontWeight: 600 }}>UNIT PRICE</div>
                                <div style={{ fontSize: '14px', fontWeight: 800, color: '#111' }}>₹{it.amount.toLocaleString('en-IN')}</div>
                              </div>
                            )}
                          </div>

                          {/* Swap section */}
                          <div style={{ borderTop: `1px solid ${it.is_renewal_item ? '#FDE68A' : '#E5E7EB'}`, padding: '12px 16px', background: it.is_renewal_item ? '#FFFDE7' : '#F9FAFB' }}>
                            {!hasSubs ? (
                              <div style={{ fontSize: '12px', color: '#9CA3AF', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span>🔒</span> No in-stock replacements available — keeping current unit
                              </div>
                            ) : (
                              <div>
                                <div style={{ fontSize: '11px', fontWeight: 700, color: '#374151', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  🔄 Replace IMEI / Serial
                                </div>

                                {/* Row 1 — two pill radios side by side */}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: selectedSwap ? '10px' : '0' }}>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', borderRadius: '9px', border: `1.5px solid ${!selectedSwap ? '#F59E0B' : '#E5E7EB'}`, background: !selectedSwap ? '#FFFBEB' : '#fff', cursor: 'pointer', minWidth: 0 }}>
                                    <input type="radio" name={`renew-swap-${it.pkg_item_id}`} checked={!selectedSwap} onChange={() => setRenewSwap(it.pkg_item_id, null)} style={{ accentColor: '#F59E0B', flexShrink: 0 }} />
                                    <div style={{ minWidth: 0 }}>
                                      <div style={{ fontSize: '12px', fontWeight: 700, color: !selectedSwap ? '#92400E' : '#374151', marginBottom: '2px' }}>Keep current</div>
                                      <div style={{ fontFamily: 'monospace', fontSize: '10px', color: '#9CA3AF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.serial_number || it.item_id}</div>
                                    </div>
                                  </label>

                                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', borderRadius: '9px', border: `1.5px solid ${selectedSwap ? '#7C3AED' : '#E5E7EB'}`, background: selectedSwap ? '#F5F3FF' : '#fff', cursor: 'pointer' }}
                                    onClick={() => { if (!selectedSwap && it.available_replacements.length > 0) setRenewSwap(it.pkg_item_id, it.available_replacements[0].id); }}>
                                    <input type="radio" name={`renew-swap-${it.pkg_item_id}`} checked={!!selectedSwap} onChange={() => {}} style={{ accentColor: '#7C3AED', flexShrink: 0 }} />
                                    <div>
                                      <div style={{ fontSize: '12px', fontWeight: 700, color: selectedSwap ? '#5B21B6' : '#374151', marginBottom: '2px' }}>Replace unit</div>
                                      <div style={{ fontSize: '10px', color: '#9CA3AF' }}>Search & select from stock</div>
                                    </div>
                                  </label>
                                </div>

                                {/* Row 2 — SearchableSelect, full width, only when replace is active */}
                                {!!selectedSwap && (
                                  <div style={{ marginBottom: '10px' }}>
                                    <SearchableSelect
                                      options={it.available_replacements}
                                      value={selectedSwap}
                                      onChange={id => setRenewSwap(it.pkg_item_id, id)}
                                    />
                                    {swapRep && (() => {
                                      const wExpired  = it.warranty_applicable && it.client_warranty_end && warrantyExpired(it.client_warranty_end);
                                      const isFree    = !wExpired; // free when: no warranty, warranty valid, or no date
                                      return (
                                        <div style={{ marginTop: '8px', borderRadius: '9px', overflow: 'hidden', border: `1.5px solid ${isFree ? '#86EFAC' : '#FCA5A5'}` }}>
                                          {/* Item + cost row */}
                                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 12px', background: isFree ? '#F0FDF4' : '#FFF5F5' }}>
                                            <div>
                                              <div style={{ fontSize: '9px', fontWeight: 700, color: isFree ? '#15803D' : '#991B1B', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '3px' }}>✓ Replacement Unit</div>
                                              <div style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 800, color: '#111' }}>{swapRep.item_id}</div>
                                              {swapRep.serial_number && <div style={{ fontSize: '10px', color: '#6B7280', marginTop: '1px' }}>SN: {swapRep.serial_number}</div>}
                                              {swapRep.ref_number && <div style={{ fontSize: '10px', color: '#6B7280', marginTop: '1px' }}>Ref: {swapRep.ref_number}</div>}
                                            </div>
                                            <div style={{ textAlign: 'right' }}>
                                              <div style={{ fontSize: '9px', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '2px' }}>Unit Cost</div>
                                              <div style={{ fontSize: '17px', fontWeight: 900, color: isFree ? '#9CA3AF' : '#DC2626', textDecoration: isFree ? 'line-through' : 'none' }}>₹{(swapRep.amount || 0).toLocaleString('en-IN')}</div>
                                              {isFree && <div style={{ fontSize: '12px', fontWeight: 800, color: '#15803D' }}>₹0</div>}
                                            </div>
                                          </div>
                                          {/* Status strip */}
                                          <div style={{ padding: '7px 12px', background: isFree ? '#DCFCE7' : '#FEE2E2', display: 'flex', alignItems: 'center', gap: '6px', borderTop: `1px solid ${isFree ? '#86EFAC' : '#FECACA'}` }}>
                                            <span style={{ fontSize: '13px' }}>{isFree ? (it.warranty_applicable ? '🛡️' : '✓') : '⚠️'}</span>
                                            <div>
                                              <div style={{ fontSize: '11px', fontWeight: 700, color: isFree ? '#15803D' : '#991B1B' }}>
                                                {!it.warranty_applicable
                                                  ? 'No warranty tracking — replacement at no charge'
                                                  : isFree
                                                    ? 'Covered by warranty — no charge'
                                                    : 'Warranty expired — unit cost added to total'}
                                              </div>
                                              {it.warranty_applicable && it.client_warranty_end && (
                                                <div style={{ fontSize: '10px', color: isFree ? '#16A34A' : '#DC2626', marginTop: '1px' }}>
                                                  Client warranty {isFree ? 'valid until' : 'expired'}: {new Date(it.client_warranty_end).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                )}

                                {/* Row 3 — Reason, only when replace is active */}
                                {selectedSwap && (
                                  <div style={{ padding: '10px 14px', background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: '9px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                      <span style={{ fontSize: '11px', fontWeight: 700, color: '#92400E', whiteSpace: 'nowrap' }}>⚠ Reason for replacing:</span>
                                      <select
                                        value={currentReason}
                                        onChange={e => setRenewSwapReason(it.pkg_item_id, e.target.value)}
                                        style={{ flex: 1, minWidth: '140px', padding: '6px 10px', border: '1.5px solid #FDBA74', borderRadius: '7px', fontSize: '12px', fontWeight: 700, color: '#92400E', background: '#FFFBF5', outline: 'none', cursor: 'pointer' }}
                                      >
                                        {REPLACE_REASONS.map(r => (
                                          <option key={r.value} value={r.value}>{r.label}</option>
                                        ))}
                                      </select>
                                      <span style={{ fontSize: '11px', color: '#9CA3AF', whiteSpace: 'nowrap' }}>
                                        Old unit → <strong style={{ color: '#92400E' }}>{reasonLabel}</strong> in inventory
                                      </span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Additional charge — only shown when items are being replaced */}
                  {Object.keys(renewSwaps).length > 0 && (
                    <div style={{ marginBottom: '16px', background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: '12px', padding: '14px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                        <span style={{ fontSize: '16px' }}>💰</span>
                        <div>
                          <div style={{ fontSize: '12px', fontWeight: 700, color: '#92400E' }}>Additional Charge (optional)</div>
                          <div style={{ fontSize: '11px', color: '#B45309' }}>e.g. installation fee, service charge, labour cost</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '16px', fontWeight: 800, color: '#92400E' }}>₹</span>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={renewAdditionalCharge}
                          onChange={e => setRenewAdditionalCharge(e.target.value)}
                          placeholder="0"
                          style={{ flex: 1, padding: '9px 14px', border: '1.5px solid #FDBA74', borderRadius: '8px', fontSize: '15px', fontWeight: 700, color: '#92400E', background: '#FFFBF5', outline: 'none', boxSizing: 'border-box' }}
                        />
                        {parseFloat(renewAdditionalCharge) > 0 && (
                          <button onClick={() => setRenewAdditionalCharge('')} style={{ padding: '8px 12px', background: '#FEE2E2', color: '#991B1B', border: '1px solid #FECACA', borderRadius: '7px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
                            Clear
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Notes */}
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 700, color: C.textMain, marginBottom: '6px' }}>Notes (optional)</div>
                    <input type="text" value={renewNote} onChange={e => setRenewNote(e.target.value)}
                      placeholder="Any notes about this renewal…"
                      style={{ width: '100%', padding: '9px 12px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }} />
                  </div>

                  {/* Cost Summary */}
                  {(() => {
                    const addCharge  = parseFloat(renewAdditionalCharge) || 0;
                    const grandTotal = (renewItem.amc_amount || 0) + renewSwapCost + addCharge;
                    return (
                      <div style={{ background: '#F9FAFB', border: `1px solid ${C.border}`, borderRadius: '12px', overflow: 'hidden' }}>
                        <div style={{ padding: '10px 16px', borderBottom: `1px solid ${C.border}`, fontSize: '11px', fontWeight: 700, color: C.textSub, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Credit Entry Summary
                        </div>
                        <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '9px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ fontSize: '13px', color: '#374151' }}>AMC Renewal</span>
                            <span style={{ fontSize: '13px', fontWeight: 700, color: '#5B21B6' }}>₹{(renewItem.amc_amount || 0).toLocaleString('en-IN')}</span>
                          </div>
                          {renewSwapCost > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span style={{ fontSize: '13px', color: '#374151' }}>Item Replacements ({Object.keys(renewSwaps).length})</span>
                              <span style={{ fontSize: '13px', fontWeight: 700, color: '#D97706' }}>+ ₹{renewSwapCost.toLocaleString('en-IN')}</span>
                            </div>
                          )}
                          {addCharge > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span style={{ fontSize: '13px', color: '#374151' }}>Additional Charge</span>
                              <span style={{ fontSize: '13px', fontWeight: 700, color: '#92400E' }}>+ ₹{addCharge.toLocaleString('en-IN')}</span>
                            </div>
                          )}
                          <div style={{ height: '1px', background: '#E5E7EB' }} />
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '14px', fontWeight: 800, color: '#111' }}>Total to Collect</span>
                            <span style={{ fontSize: '22px', fontWeight: 900, color: '#92400E' }}>
                              ₹{grandTotal.toLocaleString('en-IN')}
                            </span>
                          </div>
                        </div>
                        <div style={{ padding: '10px 16px', background: '#FFFBEB', fontSize: '12px', color: '#92400E', fontWeight: 500 }}>
                          ✓ A <strong>Pending</strong> credit entry will be created and added to the Credit List.
                        </div>
                      </div>
                    );
                  })()}
                </>
              )}
            </div>

            {/* Footer */}
            {!renewLoad && renewInfo && (
              <div style={{ padding: '14px 24px', borderTop: '1px solid #FDE68A', display: 'flex', gap: '10px', justifyContent: 'flex-end', background: '#FFFBEB', borderRadius: '0 0 18px 18px', flexShrink: 0 }}>
                <button onClick={closeRenewModal} disabled={renewing} style={{ padding: '10px 18px', background: '#fff', color: C.textSub, border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
                  Back
                </button>
                <button onClick={confirmRenew} disabled={renewing} style={{ padding: '10px 28px', background: renewing ? '#9CA3AF' : 'linear-gradient(135deg,#F59E0B,#D97706)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 800, cursor: renewing ? 'not-allowed' : 'pointer', boxShadow: '0 3px 10px rgba(245,158,11,0.35)' }}>
                  {renewing
                    ? 'Processing…'
                    : `🔄 Renew & Add ₹${((renewItem.amc_amount || 0) + renewSwapCost + (parseFloat(renewAdditionalCharge) || 0)).toLocaleString('en-IN')} to Credit`}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════
          CANCEL SUBSCRIPTION MODAL
      ══════════════════════════════════════════════════ */}
      {cancelItem && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(28,20,7,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '20px' }}>
          <div style={{ background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '420px', boxShadow: '0 24px 80px rgba(0,0,0,0.3)', border: '1px solid #FECACA' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #FECACA', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '3px', height: '18px', background: '#EF4444', borderRadius: '2px' }} />
                <span style={{ fontSize: '15px', fontWeight: 700, color: C.textMain }}>Cancel Subscription</span>
              </div>
              <button onClick={() => setCancelItem(null)} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: C.textSub }}>✕</button>
            </div>
            <div style={{ padding: '20px 24px' }}>
              <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '10px', padding: '14px 16px', marginBottom: '18px' }}>
                <div style={{ fontSize: '13px', fontWeight: 700, color: '#991B1B', marginBottom: '8px' }}>🚫 Cancelling renewal subscription</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <RRow label="Client"    value={`${cancelItem.client_name} (${cancelItem.client_ref})`} />
                  <RRow label="Package"   value={cancelItem.package_ref} mono />
                  <RRow label="Category"  value={cancelItem.category_name} />
                  {cancelItem.type_name && <RRow label="Type" value={cancelItem.type_name} />}
                  <RRow label="AMC"       value={`₹${(cancelItem.amc_amount || 0).toLocaleString('en-IN')}`} />
                  <RRow label="Due Date"  value={cancelItem.due_date} />
                </div>
              </div>
              <div>
                <div style={{ fontSize: '12px', fontWeight: 700, color: C.textMain, marginBottom: '6px' }}>Reason (optional)</div>
                <textarea value={cancelReason} onChange={e => setCancelReason(e.target.value)} rows={2}
                  placeholder="e.g. Client requested cancellation, device replaced…"
                  style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #FECACA', borderRadius: '8px', fontSize: '13px', outline: 'none', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }} />
              </div>
              <div style={{ marginTop: '14px', padding: '10px 14px', background: '#FEF3C7', borderRadius: '8px', fontSize: '12px', color: '#92400E', fontWeight: 500, lineHeight: 1.5 }}>
                ⚠ Moves to <strong>Cancelled Subscription</strong> tab — can be restored any time.
              </div>
            </div>
            <div style={{ padding: '14px 24px', borderTop: '1px solid #FECACA', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setCancelItem(null)} disabled={cancelling} style={{ padding: '10px 18px', background: C.primaryBg, color: C.textSub, border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
                Back
              </button>
              <button onClick={confirmCancel} disabled={cancelling} style={{ padding: '10px 22px', background: cancelling ? '#9CA3AF' : '#EF4444', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: cancelling ? 'not-allowed' : 'pointer' }}>
                {cancelling ? 'Cancelling…' : '🚫 Confirm Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════
          RESTORE MODAL — items + IMEI swap + credit preview
      ══════════════════════════════════════════════════ */}
      {restoreItem && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(14,10,3,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '16px' }}>
          <div style={{ background: '#fff', borderRadius: '18px', width: '100%', maxWidth: '680px', maxHeight: '93vh', display: 'flex', flexDirection: 'column', boxShadow: '0 30px 90px rgba(0,0,0,0.35)', border: '1px solid #D1FAE5' }}>

            {/* Header */}
            <div style={{ padding: '18px 24px', borderBottom: '1px solid #D1FAE5', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#F0FDF4', borderRadius: '18px 18px 0 0', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'linear-gradient(135deg,#10B981,#059669)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', color: '#fff', fontWeight: 800 }}>↩</div>
                <div>
                  <div style={{ fontSize: '15px', fontWeight: 800, color: '#065F46' }}>Restore Subscription</div>
                  <div style={{ fontSize: '11px', color: '#34D399', fontWeight: 600 }}>
                    {restoreInfo ? `${restoreInfo.client_name} · ${restoreInfo.package_ref}` : 'Loading package items…'}
                  </div>
                </div>
              </div>
              <button onClick={closeRestoreModal} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#6B7280' }}>✕</button>
            </div>

            {/* Body (scrollable) */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>

              {restoreLoad && (
                <div style={{ textAlign: 'center', padding: '60px', color: '#6B7280', fontSize: '13px' }}>
                  <div style={{ fontSize: '28px', marginBottom: '10px' }}>⏳</div>
                  Loading package items…
                </div>
              )}

              {!restoreLoad && restoreInfo && (
                <>
                  {/* Client + AMC */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '12px', marginBottom: '20px' }}>
                    <div style={{ background: '#F9FAFB', borderRadius: '10px', padding: '14px 16px' }}>
                      <div style={{ fontSize: '10px', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>Client</div>
                      <div style={{ fontSize: '15px', fontWeight: 800, color: '#111' }}>{restoreInfo.client_name}</div>
                      <div style={{ fontSize: '11px', color: '#6B7280', fontFamily: 'monospace', marginTop: '2px' }}>{restoreInfo.client_ref}</div>
                    </div>
                    <div style={{ background: '#EDE9FE', borderRadius: '10px', padding: '14px 22px', textAlign: 'center', minWidth: '140px' }}>
                      <div style={{ fontSize: '10px', fontWeight: 700, color: '#7C3AED', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>AMC</div>
                      <div style={{ fontSize: '26px', fontWeight: 900, color: '#5B21B6' }}>₹{(restoreInfo.amc_amount || 0).toLocaleString('en-IN')}</div>
                    </div>
                  </div>

                  {/* Items heading */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 700, color: C.textMain, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Package Items ({restoreInfo.items.length})
                    </div>
                    <div style={{ fontSize: '11px', color: C.textSub }}>Optionally replace any IMEI with an in-stock unit</div>
                  </div>

                  {/* Item cards */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
                    {restoreInfo.items.map(it => {
                      const selectedSwap = swaps[it.pkg_item_id] ? Number(swaps[it.pkg_item_id]) : null;
                      const swapRep      = selectedSwap ? it.available_replacements.find(r => r.id === selectedSwap) : null;
                      const hasSubs      = it.available_replacements.length > 0;

                      const currentReason = swapReasons[it.pkg_item_id] || 'damaged';
                      const reasonLabel   = REPLACE_REASONS.find(r => r.value === currentReason)?.label || currentReason;

                      return (
                        <div key={it.pkg_item_id} style={{
                          border: `1.5px solid ${it.is_cancelled_item ? '#6EE7B7' : C.border}`,
                          borderRadius: '12px', overflow: 'hidden',
                          background: it.is_cancelled_item ? '#F0FDF4' : '#FAFAFA',
                        }}>
                          {/* Item header row */}
                          <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: it.is_cancelled_item ? '#D1FAE5' : '#E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>📦</div>
                              <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '7px', flexWrap: 'wrap' }}>
                                  <span style={{ fontWeight: 700, fontSize: '13px', color: '#111' }}>{it.category_name}</span>
                                  {it.type_name && (
                                    <span style={{ fontSize: '11px', background: C.primaryBg, color: C.primaryDeep, padding: '1px 7px', borderRadius: '10px', fontWeight: 600 }}>
                                      {it.type_name}
                                    </span>
                                  )}
                                  {it.is_cancelled_item && (
                                    <span style={{ fontSize: '10px', background: '#D1FAE5', color: '#065F46', padding: '1px 7px', borderRadius: '10px', fontWeight: 700 }}>
                                      CANCELLED ITEM
                                    </span>
                                  )}
                                  {it.warranty_applicable && (
                                    <WarrantyBadgeMini dateStr={it.client_warranty_end} />
                                  )}
                                </div>
                                <div style={{ display: 'flex', gap: '14px', marginTop: '3px', flexWrap: 'wrap' }}>
                                  <span style={{ fontSize: '11px', color: '#6B7280', fontFamily: 'monospace' }}>{it.item_id}</span>
                                  {it.serial_number && (
                                    <span style={{ fontSize: '11px', color: '#6B7280' }}>
                                      SN: <span style={{ fontFamily: 'monospace', fontWeight: 600, color: '#374151' }}>{it.serial_number}</span>
                                    </span>
                                  )}
                                  {it.ref_number && (
                                    <span style={{ fontSize: '11px', color: '#6B7280' }}>
                                      Ref: <span style={{ fontFamily: 'monospace', fontWeight: 600, color: '#374151' }}>{it.ref_number}</span>
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            {it.amount > 0 && (
                              <div style={{ textAlign: 'right' }}>
                                <div style={{ fontSize: '10px', color: '#9CA3AF', fontWeight: 600 }}>UNIT PRICE</div>
                                <div style={{ fontSize: '14px', fontWeight: 800, color: '#111' }}>₹{it.amount.toLocaleString('en-IN')}</div>
                              </div>
                            )}
                          </div>

                          {/* Swap section */}
                          <div style={{ borderTop: `1px solid ${it.is_cancelled_item ? '#BBF7D0' : '#E5E7EB'}`, padding: '12px 16px', background: it.is_cancelled_item ? '#ECFDF5' : '#F9FAFB' }}>
                            {!hasSubs ? (
                              <div style={{ fontSize: '12px', color: '#9CA3AF', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span>🔒</span> No in-stock replacements available — keeping current unit
                              </div>
                            ) : (
                              <div>
                                <div style={{ fontSize: '11px', fontWeight: 700, color: '#374151', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  🔄 Replace IMEI / Serial
                                </div>

                                {/* Row 1 — two pill radios side by side */}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: selectedSwap ? '10px' : '0' }}>

                                  {/* Keep current */}
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', borderRadius: '9px', border: `1.5px solid ${!selectedSwap ? '#10B981' : '#E5E7EB'}`, background: !selectedSwap ? '#F0FDF4' : '#fff', cursor: 'pointer', minWidth: 0 }}>
                                    <input type="radio" name={`swap-${it.pkg_item_id}`} checked={!selectedSwap} onChange={() => setSwap(it.pkg_item_id, null)} style={{ accentColor: '#10B981', flexShrink: 0 }} />
                                    <div style={{ minWidth: 0 }}>
                                      <div style={{ fontSize: '12px', fontWeight: 700, color: !selectedSwap ? '#065F46' : '#374151', marginBottom: '2px' }}>Keep current</div>
                                      <div style={{ fontFamily: 'monospace', fontSize: '10px', color: '#9CA3AF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.serial_number || it.item_id}</div>
                                    </div>
                                  </label>

                                  {/* Replace unit */}
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', borderRadius: '9px', border: `1.5px solid ${selectedSwap ? '#7C3AED' : '#E5E7EB'}`, background: selectedSwap ? '#F5F3FF' : '#fff', cursor: 'pointer' }}
                                    onClick={() => { if (!selectedSwap && it.available_replacements.length > 0) setSwap(it.pkg_item_id, it.available_replacements[0].id); }}>
                                    <input type="radio" name={`swap-${it.pkg_item_id}`} checked={!!selectedSwap} onChange={() => {}} style={{ accentColor: '#7C3AED', flexShrink: 0 }} />
                                    <div>
                                      <div style={{ fontSize: '12px', fontWeight: 700, color: selectedSwap ? '#5B21B6' : '#374151', marginBottom: '2px' }}>Replace unit</div>
                                      <div style={{ fontSize: '10px', color: '#9CA3AF' }}>Search & select from stock</div>
                                    </div>
                                  </label>
                                </div>

                                {/* Row 2 — SearchableSelect, full width, only when replace is active */}
                                {!!selectedSwap && (
                                  <div style={{ marginBottom: '10px' }}>
                                    <SearchableSelect
                                      options={it.available_replacements}
                                      value={selectedSwap}
                                      onChange={id => setSwap(it.pkg_item_id, id)}
                                    />
                                    {swapRep && (() => {
                                      const wExpired  = it.warranty_applicable && it.client_warranty_end && warrantyExpired(it.client_warranty_end);
                                      const isFree    = !wExpired;
                                      return (
                                        <div style={{ marginTop: '8px', borderRadius: '9px', overflow: 'hidden', border: `1.5px solid ${isFree ? '#86EFAC' : '#FCA5A5'}` }}>
                                          {/* Item + cost row */}
                                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 12px', background: isFree ? '#F0FDF4' : '#FFF5F5' }}>
                                            <div>
                                              <div style={{ fontSize: '9px', fontWeight: 700, color: isFree ? '#15803D' : '#991B1B', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '3px' }}>✓ Replacement Unit</div>
                                              <div style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 800, color: '#111' }}>{swapRep.item_id}</div>
                                              {swapRep.serial_number && <div style={{ fontSize: '10px', color: '#6B7280', marginTop: '1px' }}>SN: {swapRep.serial_number}</div>}
                                              {swapRep.ref_number && <div style={{ fontSize: '10px', color: '#6B7280', marginTop: '1px' }}>Ref: {swapRep.ref_number}</div>}
                                            </div>
                                            <div style={{ textAlign: 'right' }}>
                                              <div style={{ fontSize: '9px', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '2px' }}>Unit Cost</div>
                                              <div style={{ fontSize: '17px', fontWeight: 900, color: isFree ? '#9CA3AF' : '#DC2626', textDecoration: isFree ? 'line-through' : 'none' }}>₹{(swapRep.amount || 0).toLocaleString('en-IN')}</div>
                                              {isFree && <div style={{ fontSize: '12px', fontWeight: 800, color: '#15803D' }}>₹0</div>}
                                            </div>
                                          </div>
                                          {/* Status strip */}
                                          <div style={{ padding: '7px 12px', background: isFree ? '#DCFCE7' : '#FEE2E2', display: 'flex', alignItems: 'center', gap: '6px', borderTop: `1px solid ${isFree ? '#86EFAC' : '#FECACA'}` }}>
                                            <span style={{ fontSize: '13px' }}>{isFree ? (it.warranty_applicable ? '🛡️' : '✓') : '⚠️'}</span>
                                            <div>
                                              <div style={{ fontSize: '11px', fontWeight: 700, color: isFree ? '#15803D' : '#991B1B' }}>
                                                {!it.warranty_applicable
                                                  ? 'No warranty tracking — replacement at no charge'
                                                  : isFree
                                                    ? 'Covered by warranty — no charge'
                                                    : 'Warranty expired — unit cost added to total'}
                                              </div>
                                              {it.warranty_applicable && it.client_warranty_end && (
                                                <div style={{ fontSize: '10px', color: isFree ? '#16A34A' : '#DC2626', marginTop: '1px' }}>
                                                  Client warranty {isFree ? 'valid until' : 'expired'}: {new Date(it.client_warranty_end).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                )}

                                {/* Row 3 — Reason, only when replace is active */}
                                {selectedSwap && (
                                  <div style={{ padding: '10px 14px', background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: '9px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                      <span style={{ fontSize: '11px', fontWeight: 700, color: '#92400E', whiteSpace: 'nowrap' }}>⚠ Reason for replacing:</span>
                                      <select
                                        value={currentReason}
                                        onChange={e => setSwapReason(it.pkg_item_id, e.target.value)}
                                        style={{ flex: 1, minWidth: '140px', padding: '6px 10px', border: '1.5px solid #FDBA74', borderRadius: '7px', fontSize: '12px', fontWeight: 700, color: '#92400E', background: '#FFFBF5', outline: 'none', cursor: 'pointer' }}
                                      >
                                        {REPLACE_REASONS.map(r => (
                                          <option key={r.value} value={r.value}>{r.label}</option>
                                        ))}
                                      </select>
                                      <span style={{ fontSize: '11px', color: '#9CA3AF', whiteSpace: 'nowrap' }}>
                                        Old unit → <strong style={{ color: '#92400E' }}>{reasonLabel}</strong> in inventory
                                      </span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Additional charge — only shown when items are being replaced */}
                  {Object.keys(swaps).length > 0 && (
                    <div style={{ marginBottom: '16px', background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: '12px', padding: '14px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                        <span style={{ fontSize: '16px' }}>💰</span>
                        <div>
                          <div style={{ fontSize: '12px', fontWeight: 700, color: '#92400E' }}>Additional Charge (optional)</div>
                          <div style={{ fontSize: '11px', color: '#B45309' }}>e.g. installation fee, service charge, labour cost</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '16px', fontWeight: 800, color: '#92400E' }}>₹</span>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={additionalCharge}
                          onChange={e => setAdditionalCharge(e.target.value)}
                          placeholder="0"
                          style={{ flex: 1, padding: '9px 14px', border: '1.5px solid #FDBA74', borderRadius: '8px', fontSize: '15px', fontWeight: 700, color: '#92400E', background: '#FFFBF5', outline: 'none', boxSizing: 'border-box' }}
                        />
                        {parseFloat(additionalCharge) > 0 && (
                          <button onClick={() => setAdditionalCharge('')} style={{ padding: '8px 12px', background: '#FEE2E2', color: '#991B1B', border: '1px solid #FECACA', borderRadius: '7px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
                            Clear
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Notes */}
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 700, color: C.textMain, marginBottom: '6px' }}>Notes (optional)</div>
                    <input type="text" value={restoreNote} onChange={e => setRestoreNote(e.target.value)}
                      placeholder="Any notes about this restoration…"
                      style={{ width: '100%', padding: '9px 12px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }} />
                  </div>

                  {/* Cost Summary */}
                  {(() => {
                    const addCharge = parseFloat(additionalCharge) || 0;
                    const grandTotal = (restoreInfo.amc_amount || 0) + swapCost + addCharge;
                    return (
                      <div style={{ background: '#F9FAFB', border: `1px solid ${C.border}`, borderRadius: '12px', overflow: 'hidden' }}>
                        <div style={{ padding: '10px 16px', borderBottom: `1px solid ${C.border}`, fontSize: '11px', fontWeight: 700, color: C.textSub, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Credit Entry Summary
                        </div>
                        <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '9px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ fontSize: '13px', color: '#374151' }}>AMC Renewal</span>
                            <span style={{ fontSize: '13px', fontWeight: 700, color: '#5B21B6' }}>₹{(restoreInfo.amc_amount || 0).toLocaleString('en-IN')}</span>
                          </div>
                          {swapCost > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span style={{ fontSize: '13px', color: '#374151' }}>Item Replacements ({Object.keys(swaps).length})</span>
                              <span style={{ fontSize: '13px', fontWeight: 700, color: '#D97706' }}>+ ₹{swapCost.toLocaleString('en-IN')}</span>
                            </div>
                          )}
                          {addCharge > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span style={{ fontSize: '13px', color: '#374151' }}>Additional Charge</span>
                              <span style={{ fontSize: '13px', fontWeight: 700, color: '#92400E' }}>+ ₹{addCharge.toLocaleString('en-IN')}</span>
                            </div>
                          )}
                          <div style={{ height: '1px', background: '#E5E7EB' }} />
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '14px', fontWeight: 800, color: '#111' }}>Total to Collect</span>
                            <span style={{ fontSize: '22px', fontWeight: 900, color: '#065F46' }}>
                              ₹{grandTotal.toLocaleString('en-IN')}
                            </span>
                          </div>
                        </div>
                        <div style={{ padding: '10px 16px', background: '#ECFDF5', fontSize: '12px', color: '#065F46', fontWeight: 500 }}>
                          ✓ A <strong>Pending</strong> credit entry will be created and the subscription restored to the active renewal list.
                        </div>
                      </div>
                    );
                  })()}
                </>
              )}
            </div>

            {/* Footer */}
            {!restoreLoad && restoreInfo && (
              <div style={{ padding: '14px 24px', borderTop: '1px solid #D1FAE5', display: 'flex', gap: '10px', justifyContent: 'flex-end', background: '#F0FDF4', borderRadius: '0 0 18px 18px', flexShrink: 0 }}>
                <button onClick={closeRestoreModal} disabled={restoring} style={{ padding: '10px 18px', background: '#fff', color: C.textSub, border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
                  Back
                </button>
                <button onClick={confirmRestore} disabled={restoring} style={{ padding: '10px 28px', background: restoring ? '#9CA3AF' : 'linear-gradient(135deg,#10B981,#059669)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 800, cursor: restoring ? 'not-allowed' : 'pointer', boxShadow: '0 3px 10px rgba(16,185,129,0.35)' }}>
                  {restoring
                    ? 'Restoring…'
                    : `↩ Restore & Add ₹${((restoreInfo.amc_amount || 0) + swapCost + (parseFloat(additionalCharge) || 0)).toLocaleString('en-IN')} to Credit`}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════
          DEALER — RAISE RENEWAL REQUEST
      ══════════════════════════════════════════════════ */}
      {reqItem && (() => {
        const cost   = reqItem.dealer_amc_amount || 0;
        const bal    = wallet ?? 0;
        const enough = bal >= cost;
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(14,10,3,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '20px' }}>
            <div style={{ background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '460px', boxShadow: '0 24px 80px rgba(0,0,0,0.3)', border: '1px solid #FDE68A' }}>

              <div style={{ padding: '18px 24px', borderBottom: '1px solid #FDE68A', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#FFFBEB', borderRadius: '16px 16px 0 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'linear-gradient(135deg,#F59E0B,#D97706)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '17px', color: '#fff' }}>📨</div>
                  <div>
                    <div style={{ fontSize: '15px', fontWeight: 800, color: '#78350F' }}>Request Renewal</div>
                    <div style={{ fontSize: '11px', color: '#D97706', fontWeight: 600 }}>Sent to admin for approval</div>
                  </div>
                </div>
                <button onClick={() => setReqItem(null)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#6B7280' }}>✕</button>
              </div>

              <div style={{ padding: '20px 24px' }}>
                <div style={{ background: '#F9FAFB', border: `1px solid ${C.border}`, borderRadius: '10px', padding: '14px 16px', marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '7px' }}>
                  <RRow label="Client"   value={`${reqItem.client_name} (${reqItem.client_ref})`} />
                  <RRow label="Device"   value={reqItem.serial_number || reqItem.item_id} mono />
                  <RRow label="Category" value={`${reqItem.category_name}${reqItem.type_name ? ` · ${reqItem.type_name}` : ''}`} />
                  <RRow label="Due Date" value={reqItem.due_date} />
                </div>

                {/* Wallet math */}
                <div style={{ borderRadius: '12px', overflow: 'hidden', border: `1.5px solid ${enough ? '#99F6E4' : '#FCA5A5'}`, marginBottom: '16px' }}>
                  <div style={{ padding: '12px 16px', background: enough ? '#F0FDFA' : '#FFF5F5', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '12px', color: '#374151' }}>Wallet balance</span>
                      <span style={{ fontSize: '13px', fontWeight: 700, color: '#0F766E' }}>₹{bal.toLocaleString('en-IN')}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '12px', color: '#374151' }}>Renewal charge</span>
                      <span style={{ fontSize: '13px', fontWeight: 700, color: '#B45309' }}>− ₹{cost.toLocaleString('en-IN')}</span>
                    </div>
                    <div style={{ height: '1px', background: enough ? '#99F6E4' : '#FCA5A5' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '13px', fontWeight: 800, color: '#111' }}>Balance after</span>
                      <span style={{ fontSize: '18px', fontWeight: 900, color: enough ? '#0F766E' : '#DC2626' }}>
                        ₹{(bal - cost).toLocaleString('en-IN')}
                      </span>
                    </div>
                  </div>
                  <div style={{ padding: '8px 16px', background: enough ? '#CCFBF1' : '#FEE2E2', display: 'flex', alignItems: 'center', gap: '7px', fontSize: '11px', fontWeight: 700, color: enough ? '#0F766E' : '#991B1B' }}>
                    <span>{enough ? '✓' : '⚠'}</span>
                    {enough
                      ? 'This amount is debited from your wallet when you raise the request.'
                      : 'Insufficient wallet balance — top up your credit to raise this request.'}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: C.textMain, marginBottom: '6px' }}>Note to admin (optional)</div>
                  <textarea value={reqNote} onChange={e => setReqNote(e.target.value)} rows={2}
                    placeholder="Anything the admin should know about this renewal…"
                    style={{ width: '100%', padding: '9px 12px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', outline: 'none', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }} />
                </div>
              </div>

              <div style={{ padding: '14px 24px', borderTop: '1px solid #FDE68A', display: 'flex', gap: '10px', justifyContent: 'flex-end', background: '#FFFBEB', borderRadius: '0 0 16px 16px' }}>
                <button onClick={() => setReqItem(null)} disabled={requesting}
                  style={{ padding: '10px 18px', background: '#fff', color: C.textSub, border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
                  Back
                </button>
                <button onClick={submitRequest} disabled={requesting || !enough}
                  style={{ padding: '10px 24px', background: (requesting || !enough) ? '#9CA3AF' : 'linear-gradient(135deg,#F59E0B,#D97706)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 800, cursor: (requesting || !enough) ? 'not-allowed' : 'pointer' }}>
                  {requesting ? 'Sending…' : !enough ? 'Insufficient Balance' : `📨 Raise Request · ₹${cost.toLocaleString('en-IN')}`}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ══════════════════════════════════════════════════
          ADMIN — COMPLETE / REJECT DEALER RENEWAL REQUEST
      ══════════════════════════════════════════════════ */}
      {actionReq && (() => {
        const { req, mode } = actionReq;
        const isDone = mode === 'complete';
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(14,10,3,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '20px' }}>
            <div style={{ background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '440px', boxShadow: '0 24px 80px rgba(0,0,0,0.3)', border: `1px solid ${isDone ? '#6EE7B7' : '#FECACA'}` }}>

              <div style={{ padding: '18px 24px', borderBottom: `1px solid ${isDone ? '#6EE7B7' : '#FECACA'}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: isDone ? '#F0FDF4' : '#FEF2F2', borderRadius: '16px 16px 0 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ width: '3px', height: '18px', background: isDone ? '#10B981' : '#EF4444', borderRadius: '2px' }} />
                  <span style={{ fontSize: '15px', fontWeight: 700, color: C.textMain }}>
                    {isDone ? 'Mark Renewal Completed' : 'Reject Renewal Request'}
                  </span>
                </div>
                <button onClick={() => setActionReq(null)} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: C.textSub }}>✕</button>
              </div>

              <div style={{ padding: '20px 24px' }}>
                <div style={{ background: '#F9FAFB', border: `1px solid ${C.border}`, borderRadius: '10px', padding: '14px 16px', marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '7px' }}>
                  <RRow label="Request" value={req.request_id} mono />
                  <RRow label="Dealer"  value={req.dealer_name} />
                  <RRow label="Client"  value={`${req.client_name} (${req.client_ref})`} />
                  <RRow label="Device"  value={req.serial_number || req.item_id} mono />
                  <RRow label="Charged" value={`₹${(req.dealer_amount || 0).toLocaleString('en-IN')}`} />
                </div>

                <div style={{ padding: '11px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: 500, lineHeight: 1.55, marginBottom: '16px',
                  background: isDone ? '#ECFDF5' : '#FEF3C7', color: isDone ? '#065F46' : '#92400E' }}>
                  {isDone
                    ? <>✓ The renewal will be applied and the due date reset from today. The dealer's wallet stays debited and their list will show <strong>Completed</strong>.</>
                    : <>⚠ <strong>₹{(req.dealer_amount || 0).toLocaleString('en-IN')}</strong> will be refunded to {req.dealer_name}'s wallet, and their list will show <strong>Rejected</strong>.</>}
                </div>

                <div>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: C.textMain, marginBottom: '6px' }}>
                    Note to dealer {isDone ? '(optional)' : '(reason)'}
                  </div>
                  <textarea value={actionNote} onChange={e => setActionNote(e.target.value)} rows={2}
                    placeholder={isDone ? 'e.g. Renewed and activated.' : 'e.g. Device not eligible for renewal yet.'}
                    style={{ width: '100%', padding: '9px 12px', border: `1.5px solid ${isDone ? '#6EE7B7' : '#FECACA'}`, borderRadius: '8px', fontSize: '13px', outline: 'none', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }} />
                </div>
              </div>

              <div style={{ padding: '14px 24px', borderTop: `1px solid ${isDone ? '#6EE7B7' : '#FECACA'}`, display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button onClick={() => setActionReq(null)} disabled={acting}
                  style={{ padding: '10px 18px', background: C.primaryBg, color: C.textSub, border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
                  Back
                </button>
                <button onClick={submitAction} disabled={acting}
                  style={{ padding: '10px 22px', background: acting ? '#9CA3AF' : (isDone ? 'linear-gradient(135deg,#10B981,#059669)' : '#EF4444'), color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: acting ? 'not-allowed' : 'pointer' }}>
                  {acting ? 'Working…' : isDone ? '✓ Confirm Completed' : '✕ Confirm Reject'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ══════════════════════════════════════════════════
          WHATSAPP RENEWAL REMINDERS (admin)
      ══════════════════════════════════════════════════ */}
      {reminder && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(6,20,16,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '16px' }}>
          <div style={{ background: '#fff', borderRadius: '18px', width: '100%', maxWidth: '620px', maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 30px 90px rgba(0,0,0,0.35)', border: '1px solid #A7F3D0' }}>

            {/* Header */}
            <div style={{ padding: '18px 24px', borderBottom: '1px solid #D1FAE5', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#F0FDF4', borderRadius: '18px 18px 0 0', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: 'linear-gradient(135deg,#25D366,#128C7E)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '19px', color: '#fff' }}>💬</div>
                <div>
                  <div style={{ fontSize: '15px', fontWeight: 800, color: '#065F46' }}>
                    Send WhatsApp Renewal Reminder{reminder.rows.length !== 1 ? 's' : ''}
                  </div>
                  <div style={{ fontSize: '11px', color: '#128C7E', fontWeight: 600 }}>
                    {reminder.rows.length} recipient{reminder.rows.length !== 1 ? 's' : ''} · sent from each device's business WhatsApp
                  </div>
                </div>
              </div>
              <button onClick={() => setReminder(null)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#6B7280' }}>✕</button>
            </div>

            {/* Body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '18px 24px' }}>
              {!remResults ? (
                <>
                  {/* Recipient preview */}
                  <div style={{ border: `1px solid ${C.border}`, borderRadius: '10px', maxHeight: '160px', overflowY: 'auto', marginBottom: '16px' }}>
                    {reminder.rows.slice(0, 60).map(r => (
                      <div key={r.uid} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', padding: '8px 12px', borderBottom: `1px solid ${C.primaryBg}`, fontSize: '12px' }}>
                        <div style={{ minWidth: 0 }}>
                          <span style={{ fontWeight: 700, color: C.textMain }}>{r.client_name}</span>
                          <span style={{ color: C.textSub }}> · {r.client_contact || 'no number'}</span>
                          {r.dealer_name && <span style={{ color: '#0F766E' }}> · 🏪 {r.dealer_name}</span>}
                        </div>
                        <span style={{ color: C.textSub, whiteSpace: 'nowrap' }}>{r.category_name} · due {r.due_date}</span>
                      </div>
                    ))}
                    {reminder.rows.length > 60 && (
                      <div style={{ padding: '8px 12px', fontSize: '11px', color: C.textSub, textAlign: 'center' }}>+{reminder.rows.length - 60} more…</div>
                    )}
                  </div>

                  {/* Message template */}
                  <div style={{ fontSize: '12px', fontWeight: 700, color: C.textMain, marginBottom: '6px' }}>Message</div>
                  <textarea value={reminderMsg} onChange={e => setReminderMsg(e.target.value)} rows={5}
                    style={{ width: '100%', padding: '11px 13px', border: `1.5px solid ${C.border}`, borderRadius: '9px', fontSize: '13px', outline: 'none', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box', lineHeight: 1.5 }} />
                  <div style={{ fontSize: '11px', color: C.textSub, marginTop: '7px', lineHeight: 1.6 }}>
                    Placeholders filled per client:{' '}
                    {['{client}', '{business}', '{category}', '{type}', '{serial}', '{ref}', '{due_date}', '{amount}', '{days}'].map(p => (
                      <code key={p} style={{ background: C.primaryBg, color: C.primaryDeep, padding: '1px 5px', borderRadius: '4px', marginRight: '4px', fontSize: '10px' }}>{p}</code>
                    ))}
                  </div>
                  <div style={{ marginTop: '12px', padding: '10px 13px', background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: '9px', fontSize: '11px', color: '#065F46', lineHeight: 1.5 }}>
                    ℹ Each message is sent using the <strong>Meta WhatsApp credentials of the business that owns that device's product</strong>. Recipients whose business has no WhatsApp set up, or who have no contact number, will be reported as skipped.
                  </div>
                </>
              ) : (
                /* Results */
                <>
                  <div style={{ display: 'flex', gap: '10px', marginBottom: '14px' }}>
                    <div style={{ flex: 1, background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
                      <div style={{ fontSize: '24px', fontWeight: 900, color: '#065F46' }}>{remResults.sent}</div>
                      <div style={{ fontSize: '11px', color: '#065F46', fontWeight: 700 }}>Sent</div>
                    </div>
                    <div style={{ flex: 1, background: remResults.failed ? '#FEF2F2' : '#F9FAFB', border: `1px solid ${remResults.failed ? '#FECACA' : C.border}`, borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
                      <div style={{ fontSize: '24px', fontWeight: 900, color: remResults.failed ? '#991B1B' : '#9CA3AF' }}>{remResults.failed}</div>
                      <div style={{ fontSize: '11px', color: remResults.failed ? '#991B1B' : '#9CA3AF', fontWeight: 700 }}>Failed / Skipped</div>
                    </div>
                  </div>
                  <div style={{ border: `1px solid ${C.border}`, borderRadius: '10px', maxHeight: '260px', overflowY: 'auto' }}>
                    {remResults.results.map(r => (
                      <div key={r.uid} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', padding: '9px 12px', borderBottom: `1px solid ${C.primaryBg}`, fontSize: '12px' }}>
                        <div style={{ minWidth: 0 }}>
                          <span style={{ fontWeight: 700, color: C.textMain }}>{r.client_name || r.uid}</span>
                          {r.business && <span style={{ color: C.textSub }}> · {r.business}</span>}
                          {!r.ok && r.reason && <div style={{ fontSize: '11px', color: '#991B1B' }}>{r.reason}</div>}
                        </div>
                        <span style={{ flexShrink: 0, fontWeight: 700, color: r.ok ? '#065F46' : '#991B1B' }}>
                          {r.ok ? '✓ sent' : '✕'}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div style={{ padding: '14px 24px', borderTop: '1px solid #D1FAE5', display: 'flex', gap: '10px', justifyContent: 'flex-end', background: '#F0FDF4', borderRadius: '0 0 18px 18px', flexShrink: 0 }}>
              <button onClick={() => setReminder(null)} disabled={sendingRem} style={{ padding: '10px 18px', background: '#fff', color: C.textSub, border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
                {remResults ? 'Close' : 'Cancel'}
              </button>
              {!remResults && (
                <button onClick={sendReminders} disabled={sendingRem} style={{ padding: '10px 26px', background: sendingRem ? '#9CA3AF' : 'linear-gradient(135deg,#25D366,#128C7E)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 800, cursor: sendingRem ? 'not-allowed' : 'pointer', boxShadow: '0 3px 10px rgba(18,140,126,0.35)' }}>
                  {sendingRem ? 'Sending…' : `💬 Send ${reminder.rows.length} Reminder${reminder.rows.length !== 1 ? 's' : ''}`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

    </LayoutComp>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function RRow({ label, value, mono }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
      <span style={{ fontSize: '11px', color: '#9CA3AF' }}>{label}</span>
      <span style={{ fontSize: '12px', fontWeight: 600, color: '#111', fontFamily: mono ? 'monospace' : 'inherit', textAlign: 'right' }}>{value}</span>
    </div>
  );
}

function DaysChip({ days, sc }) {
  const label = days < 0 ? `${Math.abs(days)}d ago` : days === 0 ? 'Today' : `${days}d left`;
  return (
    <span style={{ display: 'inline-block', padding: '4px 10px', borderRadius: '8px', fontSize: '12px', fontWeight: 700, background: sc.bg, color: sc.color, whiteSpace: 'nowrap' }}>
      {label}
    </span>
  );
}

function SummCard({ icon, label, count, amc, dot, bg, color, sub }) {
  return (
    <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '12px', padding: '18px 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
        <div style={{ fontSize: '22px' }}>{icon}</div>
        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: dot, marginTop: '4px' }} />
      </div>
      {count !== null && <div style={{ fontSize: '28px', fontWeight: 900, color: C.textMain, lineHeight: 1 }}>{count}</div>}
      {amc  !== null && <div style={{ fontSize: count !== null ? '13px' : '22px', fontWeight: 800, color, marginTop: count !== null ? '2px' : '0' }}>₹{(amc || 0).toLocaleString('en-IN')}</div>}
      <div style={{ fontSize: '12px', fontWeight: 700, color, background: bg, display: 'inline-block', padding: '2px 8px', borderRadius: '6px', margin: '6px 0 3px' }}>{label}</div>
      <div style={{ fontSize: '11px', color: C.textSub }}>{sub}</div>
    </div>
  );
}

function DetailGroup({ title, children }) {
  return (
    <div>
      <div style={{ fontSize: '10px', fontWeight: 700, color: C.textSub, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>{title}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>{children}</div>
    </div>
  );
}

function DetailItem({ label, value, mono, highlight }) {
  return (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'baseline' }}>
      <span style={{ fontSize: '11px', color: C.textSub, minWidth: '80px' }}>{label}</span>
      <span style={{ fontSize: '13px', fontWeight: 600, color: highlight || C.textMain, fontFamily: mono ? 'monospace' : 'inherit' }}>{value}</span>
    </div>
  );
}

// ── Warranty badge (inline, small) ────────────────────────────────────────────
function WarrantyBadgeMini({ dateStr }) {
  if (!dateStr) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const end = new Date(dateStr);
  const diffDays = Math.ceil((end - today) / 86400000);
  const expired  = diffDays < 0;
  const expiring = !expired && diffDays <= 30;
  const fmtDate  = end.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  const bg    = expired ? '#FEE2E2' : expiring ? '#FEF3C7' : '#DCFCE7';
  const color = expired ? '#991B1B' : expiring ? '#92400E' : '#15803D';
  const label = expired ? `Warranty expired ${fmtDate}` : expiring ? `${diffDays}d left · ${fmtDate}` : `Warranty till ${fmtDate}`;
  return (
    <span style={{ fontSize: '10px', fontWeight: 700, background: bg, color, padding: '2px 8px', borderRadius: '10px', whiteSpace: 'nowrap' }}>
      🛡️ {label}
    </span>
  );
}

// ── Searchable replacement combobox ───────────────────────────────────────────
function SearchableSelect({ options, value, onChange }) {
  const [query,  setQuery]  = useState('');
  const [open,   setOpen]   = useState(false);
  const wrapRef  = useRef(null);
  const inputRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handle = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const selected = options.find(o => o.id === value);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(o =>
      (o.serial_number || '').toLowerCase().includes(q) ||
      (o.item_id       || '').toLowerCase().includes(q) ||
      (o.ref_number    || '').toLowerCase().includes(q)
    );
  }, [options, query]);

  const handleFocus = () => {
    setQuery('');
    setOpen(true);
  };

  const handleSelect = (opt) => {
    onChange(opt.id);
    setQuery('');
    setOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
    setQuery('');
    setOpen(false);
  };

  // Display text when closed
  const displayText = selected
    ? [
        selected.item_id,
        `SN: ${selected.serial_number || 'N/A'}`,
        selected.ref_number ? `Ref: ${selected.ref_number}` : null,
        `₹${(selected.amount || 0).toLocaleString('en-IN')}`,
      ].filter(Boolean).join('  ·  ')
    : '';

  return (
    <div ref={wrapRef} style={{ position: 'relative', width: '100%' }}>
      {/* Trigger input */}
      <div style={{ display: 'flex', alignItems: 'center', border: `1.5px solid ${open ? '#7C3AED' : value ? '#A78BFA' : '#D1D5DB'}`, borderRadius: '9px', background: value && !open ? '#F5F3FF' : '#fff', overflow: 'hidden', transition: 'all 0.15s' }}>
        <span style={{ padding: '0 10px', fontSize: '12px', color: open ? '#7C3AED' : '#9CA3AF', flexShrink: 0 }}>🔍</span>
        <input
          ref={inputRef}
          value={open ? query : displayText}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={handleFocus}
          placeholder="Search by IMEI / Serial No…"
          style={{ flex: 1, padding: '9px 4px 9px 0', fontSize: '12px', fontFamily: 'monospace', fontWeight: value && !open ? 700 : 400, color: value && !open ? '#5B21B6' : '#111', border: 'none', outline: 'none', background: 'transparent', minWidth: 0 }}
        />
        {value && !open && (
          <button onClick={handleClear} title="Clear selection"
            style={{ padding: '0 8px', background: 'none', border: 'none', fontSize: '13px', color: '#A78BFA', cursor: 'pointer', lineHeight: 1 }}>
            ✕
          </button>
        )}
        <span style={{ padding: '0 12px', fontSize: '10px', color: '#9CA3AF', cursor: 'pointer', userSelect: 'none', borderLeft: '1px solid #E5E7EB', paddingTop: '9px', paddingBottom: '9px' }}
          onClick={() => { setOpen(o => !o); if (!open) inputRef.current?.focus(); }}>
          {open ? '▲' : '▼'}
        </span>
      </div>

      {/* Dropdown list */}
      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, background: '#fff', border: '1.5px solid #7C3AED', borderRadius: '10px', boxShadow: '0 8px 28px rgba(124,58,237,0.18)', zIndex: 300, overflow: 'hidden', maxHeight: '240px', overflowY: 'auto' }}>
          {/* Count header */}
          <div style={{ padding: '7px 14px', background: '#F5F3FF', borderBottom: '1px solid #EDE9FE', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '10px', fontWeight: 700, color: '#7C3AED', letterSpacing: '0.5px' }}>
              {filtered.length} UNIT{filtered.length !== 1 ? 'S' : ''} IN STOCK{query ? ` · "${query}"` : ''}
            </span>
            <span style={{ fontSize: '10px', color: '#9CA3AF' }}>tap to select</span>
          </div>

          {filtered.length === 0 ? (
            <div style={{ padding: '18px 14px', textAlign: 'center', fontSize: '12px', color: '#9CA3AF' }}>
              No matches for "{query}"
            </div>
          ) : (
            filtered.map(opt => {
              const isActive = opt.id === value;
              return (
                <div key={opt.id}
                  onClick={() => handleSelect(opt)}
                  style={{
                    padding: '11px 14px', cursor: 'pointer',
                    background: isActive ? '#F5F3FF' : '#fff',
                    borderBottom: '1px solid #F3F4F6',
                  }}
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#FAFAFA'; }}
                  onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = isActive ? '#F5F3FF' : '#fff'; }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 800, color: isActive ? '#7C3AED' : '#111', display: 'flex', alignItems: 'center', gap: '5px' }}>
                        {isActive && <span style={{ fontSize: '10px', color: '#7C3AED' }}>✓</span>}
                        {opt.item_id}
                      </div>
                      <div style={{ fontSize: '10px', color: '#6B7280', marginTop: '2px', fontFamily: 'monospace' }}>
                        SN: <span style={{ color: '#374151', fontWeight: 600 }}>{opt.serial_number || 'N/A'}</span>
                        {opt.ref_number && <span style={{ marginLeft: '8px' }}>Ref: <span style={{ color: '#374151', fontWeight: 600 }}>{opt.ref_number}</span></span>}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: '15px', fontWeight: 900, color: isActive ? '#7C3AED' : '#D97706' }}>
                        ₹{(opt.amount || 0).toLocaleString('en-IN')}
                      </div>
                      <div style={{ fontSize: '9px', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.3px' }}>unit cost</div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
